/**
 * @author Maria Osama
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-10-23 13:00:00
 * @description Contains codes of svgs
 * @filename svgs.tsx
 */
import { GRAPHQL_TYPE_RADIVISION_POST } from "@radivision/graphql/lib/ts/graphql/radivision-post";
import { GRAPHQL_TYPE_ARTICLE_STORY } from "@radivision/graphql/lib/ts/graphql/article-story";
import { GRAPHQL_TYPE_BOOK_STORY } from "@radivision/graphql/lib/ts/graphql/book-story";
import { GRAPHQL_TYPE_ONLINE_COURSE_STORY } from "@radivision/graphql/lib/ts/graphql/online-course-story";
import { GRAPHQL_TYPE_PODCAST_STORY } from "@radivision/graphql/lib/ts/graphql/podcast-story";
import { GRAPHQL_TYPE_PERSON } from "@radivision/graphql/lib/ts/graphql/person";
import { GRAPHQL_TYPE_ORIGINAL_CONTENT_STORY } from "@radivision/graphql/lib/ts/graphql/original-content-story";
import { GRAPHQL_TYPE_ENTITY } from "@radivision/graphql/lib/ts/graphql/entity";
import { GRAPHQL_TYPE_NEWSLETTER_STORY } from "@radivision/graphql/lib/ts/graphql/newsletter-story";

import React from "react";

const radivisionPostIcon: React.ReactNode = (
  <svg
    id="Layer_1"
    data-name="Layer 1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 134.2 125.8"
    className="radivision-post-icon"
  >
    <defs />
    <title>Post</title>
    <path
      className="cls-1"
      d="M118.4,0H15.8A15.8,15.8,0,0,0,0,15.8v68A15.8,15.8,0,0,0,15.8,99.6h.9v26.2L54.4,99.6h64a15.8,15.8,0,0,0,15.8-15.8v-68A15.8,15.8,0,0,0,118.4,0Zm10.8,83.8a11,11,0,0,1-10.8,10.8H52.9L21.7,116.2V94.6H15.8A11,11,0,0,1,5,83.8v-68A11,11,0,0,1,15.8,5H118.4a11,11,0,0,1,10.8,10.8Z"
    />
    <rect className="cls-1" x="35.3" y="23.5" width="63.7" height="10.56" />
    <rect className="cls-1" x="35.3" y="44.7" width="63.7" height="10.56" />
    <rect className="cls-1" x="35.3" y="66" width="63.7" height="10.56" />
  </svg>
);

const arrowsAllDirections: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512">
    <path d="M278.6 9.4c-12.5-12.5-32.8-12.5-45.3 0l-64 64c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l9.4-9.4V224H109.3l9.4-9.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-64 64c-12.5 12.5-12.5 32.8 0 45.3l64 64c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-9.4-9.4H224V402.7l-9.4-9.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l64 64c12.5 12.5 32.8 12.5 45.3 0l64-64c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-9.4 9.4V288H402.7l-9.4 9.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l64-64c12.5-12.5 12.5-32.8 0-45.3l-64-64c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l9.4 9.4H288V109.3l9.4 9.4c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-64-64z" />
  </svg>
);

const articleIcon: React.ReactNode = (
  <svg
    id="Layer_1"
    data-name="Layer 1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 97.2 118.1"
    className="articleVideo"
  >
    <defs />
    <title>Article</title>
    <path
      className="cls-1"
      d="M90.4,22.1H74.6V6.4Zm-5.8,96H95.2a2.1,2.1,0,0,0,2-1.9V23.9a4.3,4.3,0,0,0-.1-.5.3.3,0,0,1-.1-.2,1,1,0,0,0-.4-.5h0L74,.3h0a.7.7,0,0,0-.6-.3H2A2,2,0,0,0,0,1.8H0V116.2a2.1,2.1,0,0,0,2,1.9Zm8.7-3.9H4V3.7H70.6V24.1a2,2,0,0,0,2,1.9H93.3Z"
    />
    <path
      className="cls-1"
      d="M19.7,26H54.4a2,2,0,0,0,2-1.9,1.8,1.8,0,0,0-1.8-2H19.7a1.9,1.9,0,0,0-2,1.7v.3A2,2,0,0,0,19.7,26Z"
    />
    <path className="cls-1" d="M17.7,37.7a2,2,0,0,0,2,1.9H78.2a1.9,1.9,0,1,0,0-3.8H19.7A2,2,0,0,0,17.7,37.7Z" />
    <path className="cls-1" d="M19.7,53.3H79.1a2,2,0,1,0,0-3.9H19.7a2,2,0,1,0,0,3.9Z" />
    <path className="cls-1" d="M19.7,67H79.1a2,2,0,1,0,0-3.9H19.7a2,2,0,1,0,0,3.9Z" />
    <path className="cls-1" d="M19.7,80.7H79.1a2,2,0,1,0,0-3.9H19.7a2,2,0,1,0,0,3.9Z" />
    <path className="cls-1" d="M19.7,94.4H80.1a2,2,0,1,0,0-3.9H19.7a2,2,0,1,0,0,3.9Z" />
  </svg>
);

const bookIcon: React.ReactNode = (
  <svg
    id="Layer_1"
    data-name="Layer 1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 133.6 104.3"
    className="bookIcone"
  >
    <defs />
    <title>Book</title>
    <path
      className="cls-1"
      d="M121.1,14.3h-4V4.5a1.9,1.9,0,0,0-2-1.9c-32.3,0-45.2,7.6-49.2,11-4.1-3.4-17-11-49.3-11a1.9,1.9,0,0,0-2,1.9v9.8H10.7A9.9,9.9,0,0,0,.8,24.1V92.3a9.9,9.9,0,0,0,9.9,9.8H121a9.8,9.8,0,0,0,9.8-9.8V24.1A9.7,9.7,0,0,0,121.1,14.3ZM12.6,98.2H10.7a6,6,0,0,1-6-5.9V24.1a5.9,5.9,0,0,1,5.9-5.9h3.9V88.4a2,2,0,0,0,2,2c24.4,0,37.2,4.5,43.1,7.8ZM63.9,23.4V96c-5.5-3.6-18.4-9.3-45.3-9.6V6.5c31.9.3,43.1,8.6,45.3,10.6Zm4,62.8V17.1C70.1,15,81.2,6.8,113.1,6.5V86.4c-26.8.3-39.8,6-45.2,9.6Zm59,5.2v.9a5.9,5.9,0,0,1-5.9,5.9H71.9c6-3.3,18.7-7.8,43.2-7.8a2,2,0,0,0,2-2V18.2h4a5.9,5.9,0,0,1,5.8,5.9Z"
    />
  </svg>
);

const newsletterIcone: React.ReactNode = (
  <svg
    id="Layer_1"
    data-name="Layer 1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 72 72"
    className="newsletterIcon"
  >
    <title>newsletter</title>
    <path
      id="newsletter"
      className="cls-1"
      d="M64,11.67H8.77C3.93,11.67,0,14.88,0,20V52a8.32,8.32,0,0,0,8.33,8.33H63.56A8.32,8.32,0,0,0,71.88,52V20A8.53,8.53,0,0,0,64,11.67ZM6.57,20.88,34.19,42.36a3.82,3.82,0,0,0,3.94,0h0L65.75,20.88V51.56a2.35,2.35,0,0,1-2.19,2.19H8.77a2.35,2.35,0,0,1-2.2-2.19ZM36.38,35.34l-23.23-18H59.61Z"
    />
  </svg>
);

const onlineCourseIcon: React.ReactNode = (
  <svg
    id="Layer_1"
    data-name="Layer 1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 194 128.9"
    className="onlineCourseIcon"
  >
    <defs />
    <title>Online Course</title>
    <path
      className="cls-1"
      d="M181,48.8l2.1-.8c6.4-2.2,8.2-3,8.2-5s0-1.7-14.7-7.8L146.7,23.1,100.9,4.8a8.9,8.9,0,0,0-6.8-.3L73.1,13,28.6,30.9l-17.2,7L6.3,40.1c-2.1.9-3,1.3-3,2.8s.1,1.4,4.8,3.8l9.6,4.8L47.3,65.9,93.3,88a13.6,13.6,0,0,0,3.9.8,8.5,8.5,0,0,0,3.8-.9c2.9-1.4,56.7-27.3,68.3-32.3v1.9c-.1,2-.2,4.7-.2,8.4v.2a10.5,10.5,0,0,0-1.7,5.5,11.2,11.2,0,0,0,.5,3.1,5.7,5.7,0,0,0-1.1,2.2c-.7,2.9-.6,11.3-.4,20.2s.3,16.5-.3,18.9a3.7,3.7,0,0,0,2.5,4.6h2.3a2,2,0,0,0,1.1-.3l3.1-2.1,3.4,2.1a2.3,2.3,0,0,0,1.1.3h1.3a3.9,3.9,0,0,0,3.5-4.3h0a3.2,3.2,0,0,0-.3-1.4c-.5-2.3-.4-10.9-.4-18.5.2-10.5.2-17.1-.5-19.7a6.2,6.2,0,0,0-1-2.2,9.1,9.1,0,0,0-1.2-8.3V48.9Zm-3.9,18.4.5.5a6.3,6.3,0,0,1,1.5,3.9c0,2.6-2.2,6.5-4.1,6.5s-4.1-3.9-4.1-6.5,2-5.4,4.1-5.4a2.8,2.8,0,0,1,2.1,1Zm2.5,29.3c-.1,10.4-.2,16.9.5,19.5a1.9,1.9,0,0,1,.2.7h-.1l-4-2.6a2.1,2.1,0,0,0-2.3.1l-3.7,2.4c.6-2.9.5-9.4.4-19.6-.2-6.5-.3-13.8,0-17.5a5.7,5.7,0,0,0,8.8,0c.3,3.5.2,10.6.2,17Zm-.1-51.6a3.9,3.9,0,0,0-2.7,4v14l-1.8-.3a5.1,5.1,0,0,0-1.7.3c0-2.2.1-3.9.1-5.3.2-4.2.2-5.7-1.4-6.6s-1.3-.7-13.3,4.9L134.4,67.3,99.2,84.1a4.5,4.5,0,0,1-4.2,0c-3.5-1.5-68.7-33-84.9-41.1C21,38.4,53.8,25.2,74.6,16.8L95.7,8.4a4.5,4.5,0,0,1,3.6.2c5.7,2.3,69.2,27.6,85.2,34.5l-2.8,1Z"
    />
    <path
      className="cls-1"
      d="M155.2,71.6l-3.1,1.5c-.3.2-36.1,18.3-52.9,25.1a4.5,4.5,0,0,1-3.6,0C90.2,96,43,73.2,42.5,73l-3-1.4v3.3s-.1,20.2-.1,27.4a12.6,12.6,0,0,0,2.4,7.5,20.1,20.1,0,0,0,6.1,5.5,49.9,49.9,0,0,0,12.2,5.6,113.9,113.9,0,0,0,24.6,4.8c4.3.4,8.5.5,12.8.5a136.6,136.6,0,0,0,23.8-2,80.8,80.8,0,0,0,20.2-6,32.3,32.3,0,0,0,10-6.8,13.7,13.7,0,0,0,3.8-9.4c0-7.1-.1-27.1-.1-27.1Zm-6.7,36.9a27.8,27.8,0,0,1-8.8,5.9,71.3,71.3,0,0,1-19.1,5.7A137.2,137.2,0,0,1,85,121.5,104.7,104.7,0,0,1,61.4,117a44,44,0,0,1-11.2-5.2,21.1,21.1,0,0,1-5-4.4,7.5,7.5,0,0,1-1.6-5V78.2c10.7,5.1,45.6,21.9,50.4,23.9a8.5,8.5,0,0,0,6.8-.1c14-5.6,41.2-19.1,50.2-23.7.1,6,.1,18.4.1,23.7A8.8,8.8,0,0,1,148.5,108.5Z"
    />
  </svg>
);
const videoIcon: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72" className="videoIcone">
    <title>video</title>
    <g id="video_podcast" data-name="video podcast">
      <path
        className="cls-1"
        d="M15.55,9A15.53,15.53,0,0,0,0,24.55v22.9A15.53,15.53,0,0,0,15.55,63h40.9A15.53,15.53,0,0,0,72,47.45V24.55A15.53,15.53,0,0,0,56.45,9Zm0,4.91h40.9A10.51,10.51,0,0,1,67.09,24.55v22.9A10.51,10.51,0,0,1,56.45,58.09H15.55A10.51,10.51,0,0,1,4.91,47.45V24.55A10.51,10.51,0,0,1,15.55,13.91ZM30.6,24.38a2.05,2.05,0,0,0-1.88,2.05V44.59a2.43,2.43,0,0,0,1,1.64,1.51,1.51,0,0,0,1.72-.08l14.15-9a2,2,0,0,0,.9-1.72,2.12,2.12,0,0,0-.9-1.72l-14.15-9a1.28,1.28,0,0,0-.82-.33Z"
      />
    </g>
  </svg>
);
const micIcon: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <title>Video</title>
    <g id="video_podcast" data-name="video podcast">
      <path d="M16 11c0 2.209-1.791 4-4 4s-4-1.791-4-4v-7c0-2.209 1.791-4 4-4s4 1.791 4 4v7zm4-2v2c0 4.418-3.582 8-8 8s-8-3.582-8-8v-2h2v2c0 3.309 2.691 6 6 6s6-2.691 6-6v-2h2zm-7 13v-2h-2v2h-4v2h10v-2h-4z" />
    </g>
  </svg>
);
const miniPlayer: React.ReactNode = (
  <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg">
    <g>
      <rect
        stroke="currentColor"
        rx="2"
        fill="none"
        id="svg_1"
        height="14.53823"
        width="22.0038"
        y="4.36515"
        x="1.05423"
      />
      <rect
        stroke="null"
        rx="1"
        id="svg_2"
        height="5.27642"
        width="8.53209"
        y="11.6062"
        x="3.01886"
        fill="currentColor"
      />
    </g>
  </svg>
);

const personIcon: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 134 134">
    <defs />
    <title>-</title>
    <path
      className="cls-1"
      d="M122.7,131.6a38.6,38.6,0,0,0,.4-5.8c0-21.8-13.1-44.5-32-55.2l-1.7-.9-1.5,1.2a32.9,32.9,0,0,1-21,7.5,35.2,35.2,0,0,1-21.5-7.7l-1.5-1.2-1.6,1c-18.7,10.8-31.7,33.6-31.7,55.3,0,1.9.2,3.8.4,5.8H4.2a56.5,56.5,0,0,1-.4-5.8c0-23,13.2-46.9,32.9-59.5l2.4-1.6-1.5-2.4a42.1,42.1,0,0,1-7.1-23.5C30.5,18,45.8,3,66.9,3s36.2,15,36.2,35.8A40.6,40.6,0,0,1,96,62.1l-1.6,2.4,2.5,1.6c19.7,12.7,32.9,36.7,32.9,59.7a52.2,52.2,0,0,1-.3,5.8ZM66.9,9.8c-17.4,0-29.6,11.9-29.6,29S50.6,71.5,66.9,71.5,96.4,56.8,96.4,38.8,84.3,9.8,66.9,9.8Z"
    />
  </svg>
);
const originalContentIcon: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">
    <title>Artboard 1</title>
    <path d="M57.15,6.68l7.43,14.85H55.3L47.87,6.68H37.48L44.9,21.53H35.62L28.2,6.68h-10L25.6,21.53H16.32L8.89,6.68H7.42A7.44,7.44,0,0,0,0,14.1V57.9a7.44,7.44,0,0,0,7.42,7.42H64.58A7.44,7.44,0,0,0,72,57.9V6.68ZM29.29,51.51V31L44.74,41.27Z" />
  </svg>
);
const entityIcon: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 132.7 132.4">
    <defs />
    <title>-</title>
    <path
      className="cls-1"
      d="M131.5,54.4a6.4,6.4,0,0,0-.2-.7h0a.5.5,0,0,0-.2-.4L115.3,33.6a1.6,1.6,0,0,0-1.6-.8H98V15.1h4a2.1,2.1,0,0,0,2-2V3.3a2.1,2.1,0,0,0-2-2H31.1a2,2,0,0,0-2,2v9.8a2,2,0,0,0,2,2H35V32.8H19.3a1.8,1.8,0,0,0-1.6.8L2,53.3c-.2.1-.2.2-.3.4h0a1.5,1.5,0,0,0-.1.7.1.1,0,0,0-.1.1v74.8a2,2,0,0,0,2,2h126a2,2,0,0,0,2-2V54.5ZM112.8,36.7l12.6,15.8H98V36.7ZM33,5.3h67v5.8H33ZM20.2,36.7H35V52.5H7.6ZM5.5,56.5H35v70.9H5.5Zm33.4-2V15.1H94.1V127.4H86.2V109.6a1.9,1.9,0,0,0-1.9-1.9H48.8a1.9,1.9,0,0,0-2,1.9v17.8H38.9Zm11.9,72.9V111.6H82.3v15.8Zm76.7,0H98V56.5h29.5Z"
    />
    <path
      className="cls-1"
      d="M14.7,66a2,2,0,0,0,1.4-.6,2.2,2.2,0,0,0,0-2.8,2.1,2.1,0,0,0-2.8,0,1.9,1.9,0,0,0,0,2.8A1.7,1.7,0,0,0,14.7,66Z"
    />
    <circle className="cls-1" cx="14.7" cy="106.8" r="2" />
    <path className="cls-1" d="M14.7,94.1a2,2,0,0,0,0,4,2,2,0,0,0,0-4Z" />
    <circle className="cls-1" cx="14.7" cy="74.7" r="2" />
    <circle className="cls-1" cx="14.7" cy="85.4" r="2" />
    <path
      className="cls-1"
      d="M13.3,116.1a1.9,1.9,0,0,0,0,2.8,1.7,1.7,0,0,0,1.4.6,1.7,1.7,0,0,0,1.4-.7,1.9,1.9,0,0,0,.5-1.3,2.4,2.4,0,0,0-.5-1.4A2.1,2.1,0,0,0,13.3,116.1Z"
    />
    <path
      className="cls-1"
      d="M24.4,62.6a2.2,2.2,0,0,0,0,2.8,1.9,1.9,0,0,0,2.8,0,1.9,1.9,0,0,0,0-2.8A2.1,2.1,0,0,0,24.4,62.6Z"
    />
    <path className="cls-1" d="M25.8,72.7a2,2,0,0,0,0,4,2,2,0,0,0,0-4Z" />
    <path className="cls-1" d="M25.8,83.4a2,2,0,0,0,0,4,2,2,0,0,0,0-4Z" />
    <path className="cls-1" d="M25.8,104.8a2,2,0,0,0,0,4,2,2,0,0,0,0-4Z" />
    <path className="cls-1" d="M25.8,94.1a2,2,0,0,0,0,4,2,2,0,0,0,0-4Z" />
    <path
      className="cls-1"
      d="M24.4,116.1a2.2,2.2,0,0,0,0,2.8,2,2,0,0,0,1.4.6,1.7,1.7,0,0,0,1.4-.7,2,2,0,0,0,.6-1.3,2.5,2.5,0,0,0-.6-1.4A2.1,2.1,0,0,0,24.4,116.1Z"
    />
    <path
      className="cls-1"
      d="M107.2,66a2,2,0,0,0,1.4-3.4,2.1,2.1,0,0,0-2.8,0,2.5,2.5,0,0,0-.6,1.4,2.2,2.2,0,0,0,.6,1.4A2,2,0,0,0,107.2,66Z"
    />
    <circle className="cls-1" cx="107.2" cy="106.8" r="2" />
    <circle className="cls-1" cx="107.2" cy="85.4" r="2" />
    <path className="cls-1" d="M107.2,98.1a2,2,0,1,0-2-2A2,2,0,0,0,107.2,98.1Z" />
    <circle className="cls-1" cx="107.2" cy="74.7" r="2" />
    <path
      className="cls-1"
      d="M105.8,116.1a2.5,2.5,0,0,0-.6,1.4,2,2,0,0,0,.6,1.3,1.7,1.7,0,0,0,2.8,0,1.6,1.6,0,0,0,.6-1.3,2,2,0,0,0-.6-1.4A2.1,2.1,0,0,0,105.8,116.1Z"
    />
    <path
      className="cls-1"
      d="M118.3,66a1.7,1.7,0,0,0,1.4-.6,1.8,1.8,0,0,0,.6-1.4,2,2,0,0,0-.6-1.4,2,2,0,0,0-2.7,0,1.9,1.9,0,0,0,0,2.8A1.9,1.9,0,0,0,118.3,66Z"
    />
    <path className="cls-1" d="M118.3,76.7a2,2,0,1,0-1.9-2A2,2,0,0,0,118.3,76.7Z" />
    <path className="cls-1" d="M118.3,87.4a2,2,0,1,0-1.9-2A2,2,0,0,0,118.3,87.4Z" />
    <path className="cls-1" d="M118.3,98.1a2,2,0,1,0-1.9-2A2,2,0,0,0,118.3,98.1Z" />
    <path className="cls-1" d="M118.3,108.8a2,2,0,1,0-1.9-2A2,2,0,0,0,118.3,108.8Z" />
    <path
      className="cls-1"
      d="M118.3,119.5a1.5,1.5,0,0,0,1.4-.7,1.6,1.6,0,0,0,.6-1.3,2,2,0,0,0-.6-1.4,2,2,0,0,0-2.7,0,2,2,0,0,0-.6,1.4,1.6,1.6,0,0,0,.6,1.3A1.7,1.7,0,0,0,118.3,119.5Z"
    />
    <path
      className="cls-1"
      d="M59.2,62.6a2,2,0,0,0-.6,1.4,1.8,1.8,0,0,0,.6,1.4A2,2,0,1,0,62,62.6,2.1,2.1,0,0,0,59.2,62.6Z"
    />
    <path className="cls-1" d="M60.6,85.5a2,2,0,0,0-2,2,2,2,0,0,0,4,0A2,2,0,0,0,60.6,85.5Z" />
    <circle className="cls-1" cx="60.6" cy="75.7" r="2" />
    <path
      className="cls-1"
      d="M59.2,97.8a2,2,0,0,0-.6,1.4,1.8,1.8,0,0,0,.6,1.4,1.9,1.9,0,0,0,2.8,0,1.8,1.8,0,0,0,.6-1.4,2,2,0,0,0-.6-1.4A2.1,2.1,0,0,0,59.2,97.8Z"
    />
    <path
      className="cls-1"
      d="M48.8,66a2,2,0,0,0,1.4-.6,1.9,1.9,0,0,0,0-2.8,2.1,2.1,0,0,0-2.8,0,1.9,1.9,0,0,0,0,2.8A1.8,1.8,0,0,0,48.8,66Z"
    />
    <circle className="cls-1" cx="48.8" cy="75.7" r="2" />
    <path className="cls-1" d="M48.8,89.4a1.9,1.9,0,0,0,2-1.9,2,2,0,0,0-4,0A1.9,1.9,0,0,0,48.8,89.4Z" />
    <path
      className="cls-1"
      d="M48.8,101.2a2,2,0,0,0,1.4-.6,2.2,2.2,0,0,0,.6-1.4,2.5,2.5,0,0,0-.6-1.4,2.1,2.1,0,0,0-2.8,0,2,2,0,0,0-.6,1.4,2.1,2.1,0,0,0,2,2Z"
    />
    <path
      className="cls-1"
      d="M71,62.6a2.2,2.2,0,0,0,0,2.8,2,2,0,0,0,1.4.6,1.8,1.8,0,0,0,1.4-.6,1.9,1.9,0,0,0,0-2.8A2.1,2.1,0,0,0,71,62.6Z"
    />
    <path
      className="cls-1"
      d="M72.4,85.5a1.9,1.9,0,0,0-1.9,2,1.8,1.8,0,0,0,1.9,1.9,1.9,1.9,0,0,0,2-1.9A2,2,0,0,0,72.4,85.5Z"
    />
    <path
      className="cls-1"
      d="M72.4,73.8a1.8,1.8,0,0,0-1.9,1.9,1.9,1.9,0,0,0,1.9,2,2,2,0,0,0,2-2A1.9,1.9,0,0,0,72.4,73.8Z"
    />
    <path
      className="cls-1"
      d="M71,97.8a1.9,1.9,0,0,0-.5,1.4,1.7,1.7,0,0,0,.5,1.4,2,2,0,0,0,1.4.6,2.1,2.1,0,0,0,2-2,2,2,0,0,0-.6-1.4A2.1,2.1,0,0,0,71,97.8Z"
    />
    <path
      className="cls-1"
      d="M82.3,64a1.8,1.8,0,0,0,.6,1.4,1.7,1.7,0,0,0,1.4.6,1.8,1.8,0,0,0,1.3-.6,1.8,1.8,0,0,0,.6-1.4,2.4,2.4,0,0,0-.5-1.4,2.1,2.1,0,0,0-2.8,0A2,2,0,0,0,82.3,64Z"
    />
    <path className="cls-1" d="M84.3,73.8a1.9,1.9,0,0,0-2,1.9,2,2,0,1,0,3.9,0A1.9,1.9,0,0,0,84.3,73.8Z" />
    <path
      className="cls-1"
      d="M84.3,85.5a2,2,0,0,0-2,2,1.9,1.9,0,0,0,2,1.9,1.9,1.9,0,0,0,1.9-1.9A2,2,0,0,0,84.3,85.5Z"
    />
    <path
      className="cls-1"
      d="M82.9,97.8a2,2,0,0,0-.6,1.4,1.8,1.8,0,0,0,.6,1.4,1.7,1.7,0,0,0,1.4.6,1.9,1.9,0,0,0,1.9-2,2.4,2.4,0,0,0-.5-1.4A2.1,2.1,0,0,0,82.9,97.8Z"
    />
    <path
      className="cls-1"
      d="M59.2,27.4a1.8,1.8,0,0,0-.6,1.4,2,2,0,0,0,.6,1.4,2.2,2.2,0,0,0,2.8,0,2,2,0,0,0,.6-1.4,1.8,1.8,0,0,0-.6-1.4A1.9,1.9,0,0,0,59.2,27.4Z"
    />
    <path className="cls-1" d="M60.6,50.3a2,2,0,1,0,2,2A2,2,0,0,0,60.6,50.3Z" />
    <circle className="cls-1" cx="60.6" cy="40.5" r="2" />
    <path
      className="cls-1"
      d="M48.8,30.7a2.4,2.4,0,0,0,1.4-.5,2.5,2.5,0,0,0,.6-1.4,2.2,2.2,0,0,0-.6-1.4,1.9,1.9,0,0,0-2.8,0,1.8,1.8,0,0,0-.6,1.4,2,2,0,0,0,.6,1.4A2.2,2.2,0,0,0,48.8,30.7Z"
    />
    <circle className="cls-1" cx="48.8" cy="40.5" r="2" />
    <path className="cls-1" d="M48.8,54.2a2,2,0,1,0-2-1.9A2,2,0,0,0,48.8,54.2Z" />
    <path
      className="cls-1"
      d="M71,27.4a1.7,1.7,0,0,0-.5,1.4,1.9,1.9,0,0,0,.5,1.4,2.4,2.4,0,0,0,1.4.5,2.2,2.2,0,0,0,1.4-.5,2,2,0,0,0,.6-1.4,1.8,1.8,0,0,0-.6-1.4A1.9,1.9,0,0,0,71,27.4Z"
    />
    <path className="cls-1" d="M72.4,50.3a1.9,1.9,0,0,0-1.9,2,1.9,1.9,0,0,0,1.9,1.9,2,2,0,1,0,0-3.9Z" />
    <path className="cls-1" d="M72.4,38.5a2,2,0,0,0,0,4,2,2,0,0,0,0-4Z" />
    <path
      className="cls-1"
      d="M82.3,28.8a1.9,1.9,0,0,0,2,1.9,2.1,2.1,0,0,0,1.3-.5,2,2,0,0,0,.6-1.4,2.2,2.2,0,0,0-.5-1.4,1.9,1.9,0,0,0-2.8,0A1.8,1.8,0,0,0,82.3,28.8Z"
    />
    <path className="cls-1" d="M84.3,38.5a2,2,0,1,0,1.9,2A2,2,0,0,0,84.3,38.5Z" />
    <path className="cls-1" d="M84.3,50.3a2,2,0,1,0,0,3.9,2,2,0,0,0,1.9-1.9A2,2,0,0,0,84.3,50.3Z" />
  </svg>
);

const attention: React.ReactNode = (
  <svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 612.816 612.816">
    <g>
      <path
        d="M306.408,0C137.368,0,0.371,136.997,0.371,306.037s136.997,306.779,306.037,306.779s306.037-137.813,306.037-306.037
		C612.445,137.739,475.448,0,306.408,0z M306.408,583.147c-152.203,0-276.368-124.165-276.368-276.368
		S154.205,29.595,306.408,29.595S582.776,153.76,582.776,306.779S458.611,583.147,306.408,583.147z M321.613,431.76
		c0,8.827-7.195,16.021-16.021,16.021c-8.827,0-16.021-7.195-16.021-16.021c0-8.827,7.195-16.021,16.021-16.021
		S321.613,422.934,321.613,431.76z M290.387,353.211v-180.24c0-8.011,6.379-14.39,14.39-14.39c8.011,0,14.39,6.379,14.39,14.39
		v180.24c0,8.011-6.379,14.39-14.39,14.39C296.766,368.491,290.387,361.222,290.387,353.211z"
      />
    </g>
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
  </svg>
);

const audio: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 45.5">
    <title>UGC_AUDIO_Icon</title>
    <path d="M32.3,22.7V37.4A1.4,1.4,0,0,1,31,38.9a1.1,1.1,0,0,1-1.2-.4l-3.3-2.8L19.9,30H10.2a1.4,1.4,0,0,1-1-1.4V16.9a1.5,1.5,0,0,1,1.5-1.5h9.4l9.7-8.4a1.5,1.5,0,0,1,2.5.7V22.7Z" />
    <path d="M50.8,22.7a24.2,24.2,0,0,1-5.4,15.5,1.5,1.5,0,0,1-1.7.6,1.6,1.6,0,0,1-1-1.5,1.6,1.6,0,0,1,.5-1.1,8.6,8.6,0,0,0,1.4-1.8,23.6,23.6,0,0,0,3.2-13.3,22.6,22.6,0,0,0-4.7-12,1.7,1.7,0,0,1-.3-1.7,1.6,1.6,0,0,1,1.4-.8,1.7,1.7,0,0,1,1.2.6,25.3,25.3,0,0,1,3.4,5.7,24.1,24.1,0,0,1,2,8v1.8Z" />
    <path d="M45.3,22.7a19.4,19.4,0,0,1-3.8,11.6,1.5,1.5,0,0,1-1.4.6,1.3,1.3,0,0,1-1.2-.9,1.4,1.4,0,0,1,.2-1.4,25.3,25.3,0,0,0,2-3.6,16.4,16.4,0,0,0-1.9-15.9,1.6,1.6,0,0,1,.8-2.6,2,2,0,0,1,1.5.6,18.7,18.7,0,0,1,3,6.2A20.2,20.2,0,0,1,45.3,22.7Z" />
    <path d="M39.4,22.6A13.3,13.3,0,0,1,37.3,30a1.5,1.5,0,0,1-2.1.4,1.4,1.4,0,0,1-.4-2.1,14.5,14.5,0,0,0,1-1.8,10.2,10.2,0,0,0-1-9.2,1.8,1.8,0,0,1-.3-1.2,1.6,1.6,0,0,1,1.2-1.3,1.3,1.3,0,0,1,1.5.7,12.6,12.6,0,0,1,2,4.6,7.8,7.8,0,0,0,.2,1.4Z" />
  </svg>
);

const audioChecked: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 45.5">
    <title>UGC_AUDIO_Done_Icon</title>
    <path d="M53.1,21.1v-.5a27.6,27.6,0,0,0-2-8,25.3,25.3,0,0,0-3.4-5.7,1.4,1.4,0,0,0-1.2-.6,1.6,1.6,0,0,0-1.4.8,1.5,1.5,0,0,0,.2,1.7A21.6,21.6,0,0,1,50,20.9a22.4,22.4,0,0,1-3.2,13.4,12.4,12.4,0,0,1-1.4,1.8,1.6,1.6,0,0,0-.5,1.1,1.8,1.8,0,0,0,1,1.5,1.7,1.7,0,0,0,1.8-.6,24.3,24.3,0,0,0,5.4-15.6Z" />
    <path d="M46.8,17.1a19,19,0,0,0-3.1-6.2,1.5,1.5,0,0,0-1.4-.7,1.5,1.5,0,0,0-1.2,1,1.6,1.6,0,0,0,.3,1.6,16.6,16.6,0,0,1,1.9,16,20.8,20.8,0,0,1-2,3.6,1.6,1.6,0,0,0-.2,1.5,1.4,1.4,0,0,0,1.2.9,1.7,1.7,0,0,0,1.5-.6,20.4,20.4,0,0,0,3.8-11.7A20.2,20.2,0,0,0,46.8,17.1Z" />
    <path d="M41.6,21.3c-.1-.5-.1-1-.2-1.5a13.4,13.4,0,0,0-2-4.6,1.3,1.3,0,0,0-1.5-.6,1.5,1.5,0,0,0-1.2,1.2A1.7,1.7,0,0,0,37,17a10.4,10.4,0,0,1,1,9.3,11.8,11.8,0,0,1-1.1,1.9,1.5,1.5,0,0,0,2.6,1.6,13.3,13.3,0,0,0,2.1-7.4Z" />
    <path d="M19.2,27a27.3,27.3,0,0,0-8.3,6.8c-.6-1.4-1.3-2.7-1.9-4.1s-2.5-.2-2,1.1l2.7,5.7a1.1,1.1,0,0,0,2,0,25.5,25.5,0,0,1,8.7-7.6C21.7,28.3,20.5,26.3,19.2,27Z" />
    <path d="M34.4,7.2a1.5,1.5,0,0,0-2.5-.6L22.2,15c-.2.2-8.1.1-9.5.1a1.6,1.6,0,0,0-1.5,1.5V28.2a1.4,1.4,0,0,0,1,1.4,29.6,29.6,0,0,1,6.1-4.4,4.2,4.2,0,0,1,1.5-.4,3.4,3.4,0,0,1,3.1,2.5,2.9,2.9,0,0,1-.7,2.7l6.4,5.6,3.3,2.8a1.6,1.6,0,0,0,1.3.4,1.4,1.4,0,0,0,1.3-1.5V7.8A1.3,1.3,0,0,0,34.4,7.2Z" />
  </svg>
);

const book: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">
    <title>book</title>
    <path d="M65.74,6.44,45.39,7.69A12.83,12.83,0,0,0,36,12.32a12.83,12.83,0,0,0-9.39-4.63L6.26,6.44A6.27,6.27,0,0,0,0,12.7V53.39a6.27,6.27,0,0,0,6.26,6.27l20.35,1.62c2.74,0,5.3,3,6.68,5.35a3.13,3.13,0,0,0,5.42,0c1.37-2.38,3.94-5.35,6.68-5.35l20.35-1.62A6.27,6.27,0,0,0,72,53.39V12.7A6.27,6.27,0,0,0,65.74,6.44ZM26.61,55,6.26,53.39V12.7l20.35,1.63a6.26,6.26,0,0,1,6.26,6.26V57.76A8.51,8.51,0,0,0,26.61,55Zm39.13-1.63L45.39,55c-3.22.2-4.59.94-6.26,2.73V20.57a6.27,6.27,0,0,1,6.26-6.26l20.35-1.62Z" />
  </svg>
);

const caretLeft: React.ReactNode = (
  <svg viewBox="0 0 256 256">
    <polyline strokeWidth="16" points="184,16 72,128 184,240" />
  </svg>
);

const caretRight: React.ReactNode = (
  <svg viewBox="0 0 256 256">
    <polyline strokeWidth="16" points="72,16 184,128 72,240" />
  </svg>
);

const chat: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
    <path d="M0 0h24v24H0z" fill="none" />
    <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z" />
  </svg>
);

const checkmark: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
    <path d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z" />
  </svg>
);

const copy: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M18 6v-6h-18v18h6v6h18v-18h-6zm-12 10h-4v-14h14v4h-10v10zm16 6h-14v-14h14v14z" />
  </svg>
);

const market: React.ReactNode = (
  <svg
    width="24"
    height="24"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fillRule="evenodd"
    clipRule="evenodd"
  >
    <path d="M12.628 21.412l5.969-5.97 1.458 3.71-12.34 4.848-4.808-12.238 9.721 9.65zm-1.276-21.412h-9.352v9.453l10.625 10.547 9.375-9.375-10.648-10.625zm4.025 9.476c-.415-.415-.865-.617-1.378-.617-.578 0-1.227.241-2.171.804-.682.41-1.118.584-1.456.584-.361 0-1.083-.408-.961-1.218.052-.345.25-.697.572-1.02.652-.651 1.544-.848 2.276-.106l.744-.744c-.476-.476-1.096-.792-1.761-.792-.566 0-1.125.227-1.663.677l-.626-.627-.698.699.653.652c-.569.826-.842 2.021.076 2.938 1.011 1.011 2.188.541 3.413-.232.6-.379 1.083-.563 1.475-.563.589 0 1.18.498 1.078 1.258-.052.386-.26.763-.621 1.122-.451.451-.904.679-1.347.679-.418 0-.747-.192-1.049-.462l-.739.739c.463.458 1.082.753 1.735.753.544 0 1.087-.201 1.612-.597l.54.538.697-.697-.52-.521c.743-.896 1.157-2.209.119-3.247zm-9.678-7.476c.938 0 1.699.761 1.699 1.699 0 .938-.761 1.699-1.699 1.699-.938 0-1.699-.761-1.699-1.699 0-.938.761-1.699 1.699-1.699z" />
  </svg>
);

const stop: React.ReactNode = (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 1024 1024"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372 0-89 31.3-170.8 83.5-234.8l523.3 523.3C682.8 852.7 601 884 512 884zm288.5-137.2L277.2 223.5C341.2 171.3 423 140 512 140c205.4 0 372 166.6 372 372 0 89-31.3 170.8-83.5 234.8z"></path>
  </svg>
);

const chevronDown: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 71.86 40.79">
    <path d="M35.71,40.79,2.47,13.17A7.21,7.21,0,0,1,11.68,2.08l24,20L60,1.68a7.2,7.2,0,0,1,9.24,11.05Z" />
  </svg>
);

const chevronLeft: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
    <path d="M17,3h2c0.386,0,0.738,0.223,0.904,0.572s0.115,0.762-0.13,1.062L11.292,15l8.482,10.367 c0.245,0.299,0.295,0.712,0.13,1.062S19.386,27,19,27h-2c-0.3,0-0.584-0.135-0.774-0.367l-9-11c-0.301-0.369-0.301-0.898,0-1.267 l9-11C16.416,3.135,16.7,3,17,3z" />
  </svg>
);

const chevronLeftThin: React.ReactNode = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="100"
    height="100"
    viewBox="0 0 100 100"
    style={{
      fill: "#ffffff",
      margin: "auto",
    }}
  >
    <path d="M72.6 5.2c-0.3-0.3-0.7-0.3-1 0L27.4 49.5c-0.3 0.3-0.3 0.7 0 1l44.3 44.3C71.8 94.9 72 95 72.2 95s0.4-0.1 0.5-0.2c0.3-0.3 0.3-0.7 0-1L28.8 50 72.6 6.2C72.9 5.9 72.9 5.5 72.6 5.2z" />
  </svg>
);

const chevronRight: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
    <path d="M12,27h-2c-0.386,0-0.738-0.223-0.904-0.572s-0.115-0.762,0.13-1.062L17.708,15L9.226,4.633 c-0.245-0.299-0.295-0.712-0.13-1.062S9.614,3,10,3h2c0.3,0,0.584,0.135,0.774,0.367l9,11c0.301,0.369,0.301,0.898,0,1.267l-9,11 C12.584,26.865,12.3,27,12,27z" />
  </svg>
);

const chevronRightThin: React.ReactNode = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    style={{
      fill: "#ffffff",
      margin: "auto",
    }}
    width="100"
    height="100"
    viewBox="0 0 100 100"
  >
    <polygon points="27 6 71 50 27 94 28 95 73 50 28 5 " />
  </svg>
);

const bulb: React.ReactNode = (
  <svg
    version="1.0"
    xmlns="http://www.w3.org/2000/svg"
    width="880.000000pt"
    height="977.000000pt"
    viewBox="0 0 880.000000 977.000000"
    preserveAspectRatio="xMidYMid meet"
  >
    <g transform="translate(0.000000,977.000000) scale(0.100000,-0.100000)" stroke="none" fill="currentColor">
      <path d="M4345 9341 c-51 -23 -91 -70 -105 -122 -7 -26 -10 -174 -8 -427 3 -379 3 -388 25 -429 60 -111 231 -125 302 -25 39 55 42 100 39 502 l-3 385 -27 47 c-47 80 -138 108 -223 69z" />
      <path d="M1675 8238 c-108 -40 -157 -158 -106 -256 25 -48 577 -597 614 -611 42 -16 114 -13 154 5 72 35 113 127 92 207 -9 36 -52 83 -302 335 -161 161 -304 300 -318 307 -38 20 -99 26 -134 13z" />
      <path d="M7035 8231 c-25 -12 -148 -127 -332 -312 -319 -321 -324 -329 -304 -419 26 -115 154 -175 259 -121 20 11 166 149 323 308 244 246 287 294 298 331 41 144 -111 277 -244 213z" />
      <path d="M4192 7900 c-726 -70 -1369 -430 -1804 -1008 -446 -592 -606 -1354 -442 -2102 80 -364 268 -758 521 -1094 100 -133 189 -238 410 -487 227 -254 327 -382 380 -485 74 -142 74 -146 69 -550 -3 -198 -6 -377 -7 -399 -2 -68 19 -298 32 -340 33 -115 128 -240 231 -306 92 -59 169 -81 308 -87 l125 -5 43 -38 c205 -179 506 -182 707 -6 l50 44 125 6 c187 8 281 46 396 161 70 69 116 144 145 231 10 30 14 178 19 590 5 549 5 550 29 610 56 141 145 262 410 554 289 320 407 464 538 661 252 377 399 770 454 1215 18 147 16 495 -4 638 -80 572 -323 1062 -725 1463 -202 202 -417 356 -667 479 -250 123 -482 196 -747 236 -153 23 -454 32 -596 19z m538 -376 c840 -123 1537 -730 1770 -1541 132 -461 105 -1025 -70 -1468 -51 -129 -142 -304 -225 -432 -126 -193 -241 -334 -554 -678 -276 -304 -395 -479 -452 -665 l-21 -70 -761 0 -762 0 -29 83 c-76 216 -158 333 -487 699 -375 415 -527 623 -669 913 -149 304 -220 626 -221 995 -1 274 27 456 106 700 268 819 1002 1399 1870 1479 127 12 369 5 505 -15z m408 -5396 l2 -177 -489 -3 -489 -3 -43 -30 c-54 -39 -79 -86 -79 -150 0 -64 25 -111 79 -150 l43 -30 490 -3 489 -3 -6 -32 c-10 -50 -49 -102 -92 -123 -36 -18 -68 -19 -623 -19 -552 0 -588 1 -627 19 -46 20 -87 73 -98 123 -6 31 -5 32 37 43 89 24 140 88 140 176 0 84 -54 151 -139 173 l-43 12 0 173 c0 95 3 176 7 179 3 4 329 6 722 5 l716 -3 3 -177z" />
      <path d="M508 5540 c-103 -55 -130 -191 -55 -277 56 -63 53 -63 511 -63 269 0 425 4 443 11 34 13 80 55 99 91 17 32 18 105 3 145 -15 40 -48 73 -94 94 -37 17 -72 19 -455 18 -392 0 -417 -1 -452 -19z" />
      <path d="M7382 5539 c-63 -31 -97 -87 -96 -158 0 -67 20 -110 71 -148 l36 -28 434 -3 c416 -3 434 -2 472 17 108 56 130 199 44 285 -20 20 -52 40 -72 46 -21 6 -203 10 -441 10 -394 0 -406 -1 -448 -21z" />
      <path d="M2159 3369 c-19 -12 -164 -150 -321 -307 -259 -261 -286 -291 -298 -335 -23 -84 15 -169 93 -206 47 -23 100 -27 144 -11 32 12 607 581 629 622 18 35 18 121 0 156 -19 36 -65 78 -99 91 -45 17 -108 13 -148 -10z" />
      <path d="M6505 3374 c-59 -30 -86 -67 -99 -133 -15 -82 -6 -94 312 -415 164 -164 311 -304 329 -312 136 -59 282 71 243 217 -10 39 -47 79 -298 332 -183 183 -302 295 -328 307 -48 24 -118 26 -159 4z" />
    </g>
  </svg>
);

const cogs: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 344.81 334.32" className="we-do">
    <title>We</title>
    <g id="Layer_1" data-name="Layer 1">
      <path
        className="do cls-1"
        d="M186.55,159.81a62.58,62.58,0,0,0,21.9-9.08l11.72,9.91,8.11-8.1,8.1-8.11-9.91-11.71a62.89,62.89,0,0,0,9.08-21.91l15.29-1.27V86.61l-15.29-1.27a62.81,62.81,0,0,0-9.08-21.91l9.91-11.72-8.1-8.1-8.11-8.11-11.72,9.92a62.58,62.58,0,0,0-21.9-9.08l-1.28-15.29H162.35l-1.28,15.29a62.58,62.58,0,0,0-21.9,9.08L127.45,35.5l-8.1,8.11-8.11,8.1,9.92,11.72a62.64,62.64,0,0,0-9.09,21.91L96.78,86.61v22.93l15.29,1.27a62.72,62.72,0,0,0,9.09,21.91l-9.92,11.71,8.11,8.11,8.1,8.1,11.72-9.91a62.58,62.58,0,0,0,21.9,9.08l1.28,15.29h22.92Zm-46-61.74a33.23,33.23,0,1,1,33.23,33.23A33.22,33.22,0,0,1,140.58,98.07Z"
      />
      <path
        className="do cls-2"
        d="M89.29,293.13a62.81,62.81,0,0,0,22.77-6.64l10.56,11.14,8.94-7.17,8.95-7.17-8.58-12.74a62.57,62.57,0,0,0,11.42-20.78l15.34.41,1.25-11.4,1.25-11.39-15.05-2.94a62.7,62.7,0,0,0-6.64-22.76l11.14-10.57-7.17-8.94-7.17-8.94-12.74,8.57a62.57,62.57,0,0,0-20.78-11.42l.41-15.34-11.4-1.25L80.4,152.55l-2.94,15.06a62.49,62.49,0,0,0-22.76,6.63L44.13,163.1l-8.94,7.17-8.94,7.18,8.57,12.73A62.57,62.57,0,0,0,23.4,211l-15.34-.4L6.81,222,5.56,233.34l15.06,2.94a62.6,62.6,0,0,0,6.63,22.77L16.11,269.61l7.17,8.94,7.18,8.95,12.73-8.58A62.57,62.57,0,0,0,64,290.34l-.41,15.34L75,306.93l11.39,1.25ZM50.74,226.78A32.83,32.83,0,1,1,79.79,263,32.82,32.82,0,0,1,50.74,226.78Z"
      />
      <path
        className="do cls-2"
        d="M256,293.13a62.7,62.7,0,0,1-22.76-6.64l-10.57,11.14-8.94-7.17-8.94-7.17,8.57-12.74A62.75,62.75,0,0,1,202,249.77l-15.33.41-1.25-11.4-1.26-11.39,15.06-2.94a62.69,62.69,0,0,1,6.63-22.76l-11.13-10.57,7.17-8.94,7.17-8.94,12.73,8.57a62.67,62.67,0,0,1,20.78-11.42l-.4-15.34,11.39-1.25,11.4-1.25,2.93,15.06a62.49,62.49,0,0,1,22.77,6.63l10.56-11.14,8.95,7.17,8.94,7.18-8.58,12.73A62.59,62.59,0,0,1,321.92,211l15.33-.4L338.5,222l1.26,11.39-15.06,2.94a62.8,62.8,0,0,1-6.63,22.77l11.13,10.56L322,278.55l-7.17,8.95-12.73-8.58a62.67,62.67,0,0,1-20.78,11.42l.4,15.34-11.39,1.25L259,308.18Zm38.56-66.35A32.84,32.84,0,1,0,265.53,263,32.83,32.83,0,0,0,294.58,226.78Z"
      />
    </g>
  </svg>
);

const portfolioPeople: React.ReactNode = (
  <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" version="1.1">
    <g id="Layer_1">
      <title>Layer 1</title>
      <g stroke="null" id="XMLID_10_">
        <g stroke="null" id="svg_2">
          <path
            fill="currentColor"
            id="svg_1"
            d="m23.73296,16.86357c0,-1.38144 -0.41368,-2.71534 -1.19643,-3.85751c-0.563,-0.82153 -1.28801,-1.50376 -2.12818,-2.01073c0.98917,-0.93676 1.60808,-2.26078 1.60808,-3.72745c0,-2.83208 -2.30412,-5.13621 -5.13614,-5.13621c-1.67932,0 -3.25242,0.83054 -4.20794,2.19391c-0.24281,-0.03537 -0.49088,-0.05445 -0.74338,-0.05445s-0.50052,0.01914 -0.74333,0.05445c-0.95558,-1.36337 -2.52868,-2.19391 -4.20794,-2.19391c-2.83208,0 -5.1362,2.30412 -5.1362,5.13621c0,1.46668 0.61891,2.79069 1.60814,3.72745c-0.84023,0.50691 -1.56524,1.1892 -2.12818,2.01067c-0.78275,1.14224 -1.19649,2.47613 -1.19649,3.85757l0,2.85686l4.95132,0l0,2.1394l13.70546,0l0,-2.1394l4.95126,0l0,-2.85686l-0.00006,0zm-6.85266,-13.46424c2.13312,0 3.86854,1.73541 3.86854,3.86859s-1.73542,3.8686 -3.86854,3.8686c-0.03822,0 -0.07644,-0.00057 -0.11472,-0.00171c0.00292,-0.00812 0.00532,-0.01642 0.00818,-0.02453c0.06604,-0.18723 0.12112,-0.37952 0.1653,-0.57593c0.0031,-0.01395 0.00666,-0.02777 0.00963,-0.04177c0.01147,-0.05299 0.02155,-0.10648 0.03138,-0.1601c0.00463,-0.02535 0.00938,-0.05064 0.01362,-0.07606c0.00799,-0.04785 0.01522,-0.09589 0.02187,-0.14412c0.00463,-0.03353 0.00881,-0.06706 0.0128,-0.10065c0.00501,-0.04259 0.00989,-0.08524 0.01388,-0.12815c0.00405,-0.04379 0.0071,-0.08765 0.01001,-0.13157c0.00228,-0.03461 0.00513,-0.06908 0.00672,-0.10389c0.00349,-0.0746 0.00545,-0.14932 0.0057,-0.22411c0,-0.00551 0.00044,-0.0109 0.00044,-0.01648c0,-0.00298 -0.00025,-0.00596 -0.00025,-0.00894c-0.00013,-0.07637 -0.00235,-0.1523 -0.00577,-0.22798c-0.00102,-0.02199 -0.00235,-0.04392 -0.00361,-0.06585c-0.00374,-0.06433 -0.00862,-0.12834 -0.01464,-0.19204c-0.0014,-0.01445 -0.00253,-0.0289 -0.00399,-0.04329c-0.19153,-1.85127 -1.36109,-3.43584 -3.04827,-4.16877c0.72704,-0.81678 1.77864,-1.30126 2.89173,-1.30126zm-6.28985,2.37815c0.01236,-0.0045 0.02485,-0.00875 0.03721,-0.01318c0.03847,-0.01375 0.07726,-0.02681 0.11624,-0.03942c0.02954,-0.00944 0.05926,-0.01857 0.08917,-0.02731c0.02174,-0.0064 0.04355,-0.01255 0.06541,-0.01864c0.03904,-0.01071 0.07828,-0.02117 0.11789,-0.03074c0.29282,-0.07111 0.59818,-0.10971 0.91249,-0.10971c0.31456,0.00025 0.62004,0.03891 0.91292,0.11003c0.03936,0.0095 0.07834,0.0199 0.11719,0.03055c0.02218,0.00615 0.04436,0.01236 0.06636,0.01889c0.02954,0.00868 0.05894,0.01768 0.08816,0.027c0.03949,0.01274 0.07872,0.02593 0.11763,0.03986c0.01198,0.00424 0.02396,0.00831 0.03587,0.01268c1.29378,0.47846 2.26381,1.62989 2.48336,3.02723c0.00171,0.01115 0.00336,0.02237 0.00501,0.03359c0.00703,0.04735 0.01292,0.09507 0.01818,0.14293c0.0019,0.01762 0.00393,0.03518 0.00564,0.05286c0.00419,0.04367 0.00729,0.08765 0.01008,0.13176c0.00114,0.01889 0.00272,0.03771 0.00361,0.05666c0.00267,0.05635 0.00412,0.11301 0.00431,0.16999c0,0.00494 0.00032,0.00977 0.00032,0.01471c0,0.00228 -0.00013,0.00463 -0.00013,0.00691c-0.00013,0.05514 -0.00177,0.1099 -0.00418,0.16447c-0.00076,0.01718 -0.00152,0.03436 -0.00253,0.05147c-0.00298,0.05273 -0.00691,0.10521 -0.01198,0.15731c-0.00197,0.02015 -0.00463,0.04025 -0.00691,0.06033c-0.00387,0.03372 -0.00818,0.06738 -0.01287,0.10084c-0.00367,0.02605 -0.00735,0.05216 -0.01154,0.07809c-0.0064,0.03948 -0.0135,0.07871 -0.0211,0.11782c-0.00678,0.0348 -0.0142,0.06946 -0.02193,0.10408c-0.00463,0.02085 -0.00944,0.0417 -0.01445,0.06243c-0.01236,0.0514 -0.02593,0.10248 -0.04037,0.15338c-0.00577,0.02021 -0.01115,0.04056 -0.01724,0.06059c-0.00881,0.02915 -0.01832,0.05806 -0.02777,0.08696c-0.0076,0.02313 -0.0154,0.04614 -0.02345,0.06908c-0.00912,0.02605 -0.01812,0.05216 -0.02776,0.07802c-0.01641,0.04393 -0.03365,0.08746 -0.05159,0.13063c-0.00799,0.01926 -0.01648,0.03834 -0.02478,0.05755c-0.01565,0.03594 -0.03175,0.07169 -0.04848,0.10704c-0.00197,0.00419 -0.00393,0.00837 -0.00589,0.01255c-0.00602,0.01255 -0.01198,0.02516 -0.01813,0.03771c-0.01876,0.03828 -0.03829,0.07612 -0.05831,0.11364c-0.0078,0.01458 -0.01534,0.02935 -0.02332,0.04386c-0.00051,0.00095 -0.00102,0.00184 -0.00152,0.00272c-0.02193,0.03994 -0.04481,0.07923 -0.06807,0.11827c-0.00926,0.01546 -0.01838,0.031 -0.02783,0.04627c-0.02535,0.04113 -0.0514,0.08182 -0.07821,0.12188c-0.01667,0.02491 -0.0341,0.04931 -0.05134,0.07384c-0.01464,0.02079 -0.02922,0.04163 -0.04424,0.06211c-0.01945,0.02649 -0.03929,0.05267 -0.05945,0.07871c-0.01369,0.01768 -0.0275,0.03524 -0.04144,0.05273c-0.0213,0.02662 -0.04278,0.05311 -0.06471,0.07917c-0.01363,0.01616 -0.02764,0.032 -0.04151,0.04792c-0.02282,0.02617 -0.04557,0.05242 -0.06902,0.07795c-0.01395,0.01515 -0.0284,0.02973 -0.04259,0.04469c-0.03816,0.04025 -0.0772,0.0796 -0.117,0.1182c-0.02364,0.02288 -0.04716,0.04582 -0.07137,0.06808c-0.01673,0.0154 -0.03391,0.03035 -0.05096,0.04551c-0.02776,0.02466 -0.05572,0.04905 -0.08417,0.07288c-0.01445,0.01211 -0.02903,0.02409 -0.04367,0.036c-0.03441,0.02801 -0.06927,0.05546 -0.1047,0.08233c-0.00964,0.00729 -0.01914,0.01471 -0.02884,0.02193c-0.64395,0.47871 -1.44101,0.76247 -2.30324,0.76247c-0.86229,0 -1.65949,-0.28382 -2.30343,-0.76266c-0.0095,-0.00709 -0.01882,-0.01438 -0.02827,-0.02155c-0.03556,-0.02693 -0.07061,-0.05457 -0.10515,-0.08271c-0.01451,-0.01185 -0.02903,-0.0237 -0.04335,-0.03575c-0.02858,-0.02396 -0.0566,-0.04848 -0.08449,-0.0732c-0.01692,-0.01508 -0.03403,-0.02992 -0.0507,-0.04525c-0.02428,-0.02231 -0.04785,-0.04532 -0.07156,-0.0682c-0.03999,-0.03872 -0.0791,-0.07821 -0.11738,-0.11858c-0.014,-0.01477 -0.02833,-0.02915 -0.04209,-0.04412c-0.02351,-0.0256 -0.04627,-0.05191 -0.06908,-0.07808c-0.01388,-0.01591 -0.02782,-0.03169 -0.04145,-0.04785c-0.02199,-0.02605 -0.04341,-0.05254 -0.0647,-0.0791c-0.01395,-0.01749 -0.02783,-0.03505 -0.04151,-0.0528c-0.02009,-0.02598 -0.03994,-0.05216 -0.05939,-0.07865c-0.01503,-0.02054 -0.02966,-0.04145 -0.04431,-0.0623c-0.01724,-0.02447 -0.03461,-0.04881 -0.05121,-0.07365c-0.02693,-0.04025 -0.05311,-0.08106 -0.07852,-0.12232c-0.00932,-0.01515 -0.01832,-0.03042 -0.02745,-0.0457c-0.02364,-0.03961 -0.04677,-0.07941 -0.06902,-0.11991c-0.00817,-0.0149 -0.01591,-0.03004 -0.02396,-0.045c-0.02002,-0.03764 -0.03961,-0.07561 -0.05844,-0.11395c-0.00672,-0.01363 -0.01324,-0.02738 -0.01977,-0.04107c-0.00102,-0.00209 -0.00197,-0.00418 -0.00292,-0.00628c-0.01756,-0.03702 -0.03435,-0.07441 -0.05077,-0.11212c-0.0078,-0.01794 -0.01572,-0.03581 -0.0232,-0.05381c-0.01838,-0.04411 -0.03594,-0.0886 -0.05273,-0.13348c-0.00907,-0.02421 -0.01743,-0.04867 -0.02599,-0.07307c-0.00881,-0.02504 -0.0173,-0.05026 -0.0256,-0.07555c-0.00894,-0.02739 -0.01794,-0.05476 -0.02631,-0.08227c-0.01249,-0.04113 -0.02428,-0.08252 -0.03537,-0.12416c-0.00779,-0.02922 -0.01483,-0.05869 -0.02199,-0.0881c-0.00558,-0.02313 -0.01096,-0.04627 -0.0161,-0.06953c-0.00634,-0.02852 -0.01274,-0.05698 -0.01844,-0.08562c-0.00881,-0.04462 -0.01686,-0.0895 -0.02409,-0.13462c-0.00393,-0.02434 -0.00735,-0.04874 -0.01084,-0.07308c-0.00513,-0.03606 -0.00976,-0.07231 -0.01388,-0.10869c-0.00202,-0.01794 -0.00437,-0.03587 -0.00621,-0.05381c-0.00526,-0.05343 -0.00925,-0.10723 -0.01229,-0.16124c-0.00089,-0.01553 -0.00159,-0.03106 -0.00228,-0.04658c-0.0026,-0.05774 -0.00443,-0.11567 -0.00443,-0.17398c0,-0.0625 0.00177,-0.12454 0.00475,-0.18634c0.00082,-0.01679 0.00222,-0.03347 0.00317,-0.0502c0.00279,-0.04639 0.00615,-0.09266 0.01059,-0.13867c0.00152,-0.01603 0.00336,-0.032 0.00507,-0.04798c0.00545,-0.04981 0.0116,-0.09938 0.01895,-0.14862c0.0014,-0.00957 0.00279,-0.01921 0.00432,-0.02877c0.21885,-1.39817 1.18907,-2.55036 2.48336,-3.02913zm-7.48134,1.49045c0,-2.13312 1.73542,-3.86859 3.8686,-3.86859c1.11315,0 2.16469,0.48454 2.89173,1.30113c-1.68775,0.73318 -2.85757,2.31851 -3.04853,4.17054c-0.00133,0.01255 -0.00228,0.02516 -0.00348,0.03777c-0.00634,0.06598 -0.01134,0.13222 -0.01515,0.19882c-0.00114,0.02029 -0.0024,0.0405 -0.00336,0.06085c-0.00361,0.07929 -0.00608,0.15883 -0.00608,0.23894c0,0.08087 0.00235,0.16155 0.00615,0.24217c0.00152,0.03245 0.00418,0.06458 0.00628,0.09685c0.0031,0.04639 0.00628,0.09279 0.01058,0.13906c0.0038,0.04113 0.0085,0.08201 0.01331,0.12289c0.00418,0.03562 0.00862,0.07111 0.01356,0.1066c0.00647,0.04658 0.01344,0.09299 0.02111,0.13919c0.00463,0.02744 0.00969,0.05482 0.0147,0.0822c0.00951,0.05172 0.01921,0.10331 0.03023,0.15446c0.00367,0.01686 0.00786,0.03359 0.01166,0.05038c0.04304,0.19034 0.09647,0.37667 0.1601,0.55838c0.00405,0.01173 0.0076,0.02358 0.01173,0.03524c-0.03822,0.00114 -0.0765,0.00171 -0.11466,0.00171c-2.13306,-0.00006 -3.86847,-1.73547 -3.86847,-3.8686zm3.02989,8.08104c-0.01395,0.02199 -0.02725,0.04437 -0.04094,0.06649c-0.02896,0.04677 -0.05768,0.09368 -0.08543,0.14102c-0.01553,0.02643 -0.03049,0.05305 -0.04557,0.07967c-0.02497,0.04392 -0.04963,0.08797 -0.07359,0.1324c-0.01508,0.02795 -0.02992,0.05603 -0.04462,0.08411c-0.02307,0.04417 -0.04557,0.08861 -0.06763,0.13316c-0.01388,0.02801 -0.0277,0.05596 -0.04113,0.08411c-0.02244,0.04697 -0.04405,0.0943 -0.0654,0.14171c-0.0116,0.0258 -0.02351,0.05141 -0.03473,0.07733c-0.02472,0.05679 -0.0483,0.11402 -0.07143,0.17145c-0.00678,0.01679 -0.01401,0.0334 -0.0206,0.05019c-0.02935,0.0746 -0.05742,0.1497 -0.08404,0.22519c-0.00608,0.01731 -0.01154,0.0348 -0.01756,0.05211c-0.02003,0.05818 -0.03955,0.11656 -0.05799,0.17518c-0.00843,0.02693 -0.01622,0.05399 -0.02434,0.081c-0.0149,0.04969 -0.02954,0.09944 -0.04329,0.14951c-0.00817,0.02979 -0.01597,0.05964 -0.02377,0.08956c-0.01254,0.04804 -0.02459,0.09621 -0.03599,0.14457c-0.00729,0.03061 -0.01439,0.06122 -0.02117,0.0919c-0.0109,0.049 -0.0211,0.09817 -0.03092,0.14742c-0.00589,0.0296 -0.01198,0.0592 -0.01749,0.08892c-0.00996,0.05361 -0.01889,0.10749 -0.02758,0.16143c-0.00405,0.02535 -0.00855,0.05058 -0.01236,0.07593c-0.01179,0.07922 -0.0225,0.15864 -0.0315,0.23844c-0.00013,0.00095 -0.00019,0.0019 -0.00025,0.00286c-0.00818,0.07257 -0.01522,0.14527 -0.02111,0.21821l-3.70596,0l0,-1.58925c0,-2.17128 1.24821,-4.12054 3.20374,-5.04703c0.71239,0.37445 1.5222,0.5876 2.38132,0.5876c0.24845,0 0.49766,-0.01838 0.74498,-0.05476c0.03853,0.05495 0.0777,0.10934 0.11826,0.1627c0.01369,0.01793 0.02789,0.03542 0.04183,0.05317c0.04006,0.05122 0.08068,0.10186 0.12264,0.15148c0.01718,0.02035 0.0348,0.04025 0.05229,0.06034c0.03929,0.04512 0.07923,0.08969 0.12005,0.13335c0.02142,0.02301 0.04322,0.04563 0.06502,0.06827c0.03733,0.03847 0.07518,0.07637 0.11364,0.1137c0.01445,0.01401 0.02808,0.02884 0.04266,0.04272c-0.05508,0.03327 -0.10965,0.06737 -0.16371,0.10217c-0.00881,0.00564 -0.01743,0.0116 -0.02618,0.0173c-0.05407,0.03511 -0.10755,0.07099 -0.16054,0.10756c-0.01832,0.01268 -0.03657,0.02547 -0.05483,0.03828c-0.04722,0.03328 -0.09393,0.06712 -0.14026,0.10154c-0.01464,0.0109 -0.02947,0.02155 -0.04398,0.03257c-0.05894,0.04456 -0.11719,0.08994 -0.17468,0.13633c-0.01255,0.01014 -0.02485,0.0206 -0.0374,0.03087c-0.04576,0.03745 -0.09107,0.07555 -0.13583,0.11421c-0.01737,0.01495 -0.03461,0.03004 -0.05184,0.04519c-0.04481,0.03948 -0.08905,0.07955 -0.13284,0.12017c-0.01141,0.01058 -0.02307,0.02098 -0.03441,0.03169c-0.05375,0.05058 -0.10667,0.10211 -0.15883,0.15452c-0.01357,0.01362 -0.02681,0.0275 -0.04025,0.0412c-0.03993,0.04075 -0.07941,0.08195 -0.1184,0.12371c-0.01546,0.01661 -0.03092,0.03322 -0.04627,0.05001c-0.04386,0.04792 -0.08708,0.09646 -0.12961,0.14571c-0.00665,0.00773 -0.01356,0.01521 -0.02021,0.02301c-0.04849,0.0566 -0.0959,0.11427 -0.14267,0.17252c-0.01305,0.01623 -0.02579,0.03271 -0.03866,0.04912c-0.03518,0.04468 -0.06984,0.08981 -0.10394,0.13538c-0.01318,0.01762 -0.02643,0.03511 -0.03942,0.05286c-0.04462,0.06084 -0.08867,0.1222 -0.13145,0.18462c-0.04595,0.06706 -0.09044,0.13494 -0.1338,0.20332zm0.20491,5.24332l0,-0.87179l0,-0.71746c0,-0.07035 0.00133,-0.14045 0.00393,-0.21036c0.00082,-0.02301 0.00267,-0.04582 0.00381,-0.06877c0.00228,-0.04665 0.00443,-0.09336 0.00785,-0.13982c0.00209,-0.02821 0.00526,-0.05616 0.0078,-0.0843c0.00367,-0.04094 0.00697,-0.08195 0.01154,-0.12264c0.00336,-0.02979 0.00773,-0.05939 0.01154,-0.08911c0.00494,-0.03872 0.00957,-0.07751 0.01527,-0.11605c0.00463,-0.03067 0.01021,-0.06109 0.01528,-0.09165c0.00621,-0.03714 0.01198,-0.07441 0.01895,-0.11136c0.00589,-0.03125 0.01274,-0.06217 0.01914,-0.0933c0.00735,-0.03587 0.01439,-0.07188 0.02244,-0.1075c0.0071,-0.03143 0.01522,-0.06255 0.02282,-0.09386c0.00856,-0.03486 0.01674,-0.06991 0.02593,-0.10458c0.00837,-0.0315 0.01762,-0.06269 0.02649,-0.09393c0.00964,-0.03404 0.01902,-0.06814 0.02928,-0.10192c0.00957,-0.03144 0.02009,-0.06262 0.03017,-0.09387c0.01077,-0.03315 0.02124,-0.06635 0.03258,-0.09925c0.01077,-0.03111 0.02237,-0.06198 0.03365,-0.09285c0.01192,-0.03257 0.02358,-0.06528 0.03606,-0.0976c0.01192,-0.03074 0.02459,-0.0611 0.03695,-0.09165c0.01299,-0.03188 0.0258,-0.06395 0.03936,-0.09558c0.01311,-0.03049 0.02693,-0.06066 0.04056,-0.09082c0.014,-0.03112 0.02789,-0.06237 0.04246,-0.09318c0.01426,-0.0301 0.02922,-0.05983 0.04399,-0.08968c0.01508,-0.03042 0.02998,-0.06091 0.04557,-0.09102c0.0154,-0.02966 0.03144,-0.05894 0.04735,-0.08828c0.0161,-0.02973 0.032,-0.05945 0.04861,-0.08886c0.01648,-0.02915 0.03359,-0.05787 0.05051,-0.0867c0.01711,-0.02903 0.0341,-0.05812 0.05178,-0.08689c0.01756,-0.02859 0.03568,-0.05673 0.05375,-0.085c0.01813,-0.02833 0.03613,-0.05666 0.05476,-0.08467s0.03778,-0.05565 0.05692,-0.08328c0.01907,-0.02757 0.03809,-0.05514 0.05761,-0.0824c0.01964,-0.02738 0.03986,-0.05438 0.06009,-0.08144c0.02002,-0.02681 0.03999,-0.05362 0.06046,-0.08005c0.02067,-0.02669 0.04183,-0.05299 0.063,-0.07936c0.02098,-0.02611 0.04202,-0.05216 0.06344,-0.07789c0.02162,-0.02586 0.04367,-0.0514 0.06573,-0.07688c0.02199,-0.02542 0.04405,-0.05083 0.06655,-0.07593c0.0225,-0.0251 0.04551,-0.04982 0.06845,-0.07453c0.02294,-0.02466 0.04589,-0.04919 0.06927,-0.0734c0.02345,-0.02434 0.04735,-0.04829 0.0713,-0.07219c0.0239,-0.0239 0.04792,-0.04766 0.07226,-0.07118c0.02427,-0.02339 0.04893,-0.04646 0.07371,-0.06946c0.02485,-0.02307 0.04981,-0.04608 0.0751,-0.06877c0.02523,-0.02257 0.0507,-0.04481 0.07637,-0.06693c0.02574,-0.02225 0.05165,-0.04436 0.07783,-0.06617c0.02605,-0.02167 0.05235,-0.04303 0.07884,-0.06427c0.02669,-0.02142 0.05356,-0.04265 0.08068,-0.06363c0.02681,-0.02066 0.05387,-0.04107 0.08106,-0.06135c0.02764,-0.0206 0.05546,-0.04094 0.0836,-0.06104c0.0275,-0.01971 0.05527,-0.0391 0.08322,-0.05837c0.02865,-0.01971 0.05742,-0.03917 0.08645,-0.05837c0.0282,-0.01864 0.05666,-0.03707 0.08524,-0.05526c0.0296,-0.01883 0.05939,-0.0374 0.08937,-0.05565c0.0289,-0.01762 0.05799,-0.03505 0.08727,-0.05216c0.03042,-0.01781 0.0611,-0.03537 0.09196,-0.05267c0.0296,-0.0166 0.05939,-0.03302 0.08937,-0.04912c0.03138,-0.01686 0.063,-0.03341 0.09482,-0.04976c0.03017,-0.01553 0.06046,-0.0308 0.09101,-0.04582c0.02402,-0.01179 0.0483,-0.0232 0.07257,-0.03467c0.71233,0.37445 1.52227,0.58753 2.38132,0.58753c0.85911,0 1.66898,-0.21308 2.38132,-0.58753c0.02409,0.01141 0.04817,0.02275 0.072,0.03441c0.03093,0.01521 0.06154,0.03068 0.09209,0.04633c0.03144,0.01616 0.06274,0.03252 0.0938,0.04919c0.0303,0.01629 0.0604,0.03289 0.09032,0.04969c0.03048,0.01711 0.06078,0.03441 0.09082,0.05203c0.02973,0.01737 0.0592,0.03505 0.08848,0.05292c0.0296,0.01806 0.05894,0.03632 0.0881,0.05489c0.02903,0.01844 0.0578,0.03707 0.08639,0.05603c0.02871,0.01895 0.05717,0.03822 0.08543,0.05768c0.0282,0.01946 0.05629,0.03905 0.08411,0.05894c0.02782,0.0199 0.05533,0.04006 0.08271,0.06047c0.0275,0.0204 0.05482,0.04101 0.08189,0.06192c0.02687,0.02079 0.05349,0.04183 0.07998,0.06306c0.02662,0.02136 0.05311,0.04285 0.07935,0.06465c0.02605,0.02168 0.05178,0.04367 0.07739,0.06579c0.0258,0.02225 0.05146,0.04462 0.07682,0.06738c0.0251,0.0225 0.04988,0.04532 0.07453,0.06819c0.02497,0.0232 0.04988,0.04652 0.07441,0.07016c0.02415,0.02326 0.04792,0.04684 0.07156,0.07048c0.02409,0.02409 0.04817,0.04823 0.07181,0.07269c0.0232,0.02409 0.04608,0.04842 0.06883,0.07289c0.0232,0.0249 0.04633,0.04988 0.06902,0.07517c0.02231,0.02485 0.04418,0.05 0.06598,0.07523c0.02225,0.02567 0.04449,0.0514 0.06623,0.07752c0.02136,0.02554 0.04221,0.05146 0.06306,0.07738c0.02129,0.0265 0.04259,0.05292 0.06338,0.0798c0.0204,0.0263 0.04025,0.05299 0.06015,0.07967c0.02028,0.02712 0.04056,0.05432 0.06033,0.08182c0.01946,0.02706 0.03841,0.05457 0.05736,0.08201c0.01921,0.02776 0.03848,0.05552 0.05717,0.08366c0.0185,0.02782 0.03637,0.05596 0.05438,0.0841c0.01819,0.02846 0.03645,0.05685 0.05413,0.08563c0.01749,0.02846 0.03435,0.05736 0.05134,0.08613c0.01711,0.02903 0.03435,0.05799 0.05089,0.08734c0.01655,0.02928 0.03239,0.05894 0.04842,0.08848c0.01597,0.02947 0.03207,0.05888 0.04747,0.0886c0.01559,0.03004 0.03042,0.06053 0.04551,0.09089c0.01477,0.02985 0.02979,0.05964 0.04405,0.08981c0.01457,0.0308 0.02839,0.06193 0.04233,0.09299c0.01369,0.03035 0.02758,0.06052 0.04069,0.09114c0.0135,0.03138 0.02612,0.06313 0.03905,0.09482c0.01255,0.0308 0.02535,0.06148 0.03733,0.09253c0.01242,0.03213 0.02402,0.06465 0.03587,0.0971c0.01134,0.031 0.02294,0.06186 0.03372,0.09311c0.01141,0.03303 0.02193,0.06643 0.03271,0.0997c0.01008,0.03112 0.02053,0.06211 0.03004,0.09349c0.01033,0.03378 0.01964,0.06794 0.02935,0.10198c0.00887,0.03124 0.01812,0.06236 0.02649,0.09387c0.00919,0.03467 0.01737,0.06965 0.02593,0.10458c0.00766,0.03124 0.01572,0.06243 0.02282,0.09386c0.00804,0.03562 0.01502,0.07156 0.02244,0.10737c0.0064,0.03112 0.01325,0.06211 0.01914,0.09342c0.0069,0.03664 0.01261,0.07358 0.01882,0.11047c0.00513,0.03087 0.01084,0.06155 0.01547,0.0926c0.0057,0.03822 0.01027,0.07675 0.01521,0.11517c0.00386,0.02997 0.00831,0.05976 0.01166,0.08987c0.00456,0.04069 0.00786,0.0817 0.01154,0.12264c0.00254,0.02814 0.0057,0.05609 0.0078,0.0843c0.00348,0.04646 0.00558,0.09323 0.00785,0.13994c0.00114,0.02288 0.00298,0.0457 0.00381,0.06864c0.00259,0.06991 0.00393,0.14001 0.00393,0.21036l0,0.7174l0,0.87192l-11.17006,0zm12.4158,-2.13946c-0.01242,-0.15439 -0.0303,-0.30784 -0.05299,-0.46033c-0.00362,-0.02415 -0.00793,-0.04811 -0.01179,-0.07219c-0.00881,-0.0552 -0.018,-0.11028 -0.02821,-0.16516c-0.00539,-0.0289 -0.01128,-0.05768 -0.01705,-0.08651c-0.00995,-0.05014 -0.02034,-0.10014 -0.03143,-0.14996c-0.00672,-0.03004 -0.01369,-0.06008 -0.02079,-0.09c-0.0116,-0.04893 -0.02377,-0.09767 -0.03644,-0.14628c-0.00767,-0.02947 -0.01534,-0.05888 -0.02345,-0.08829c-0.01388,-0.05058 -0.02871,-0.1009 -0.0438,-0.15116c-0.00798,-0.0265 -0.01559,-0.05305 -0.0239,-0.07948c-0.0187,-0.05951 -0.03847,-0.11871 -0.05882,-0.17778c-0.0057,-0.01648 -0.0109,-0.03315 -0.01673,-0.04963c-0.02674,-0.07574 -0.05482,-0.15103 -0.08429,-0.22589c-0.00634,-0.01603 -0.01325,-0.03188 -0.01971,-0.04792c-0.02339,-0.05812 -0.04735,-0.11611 -0.07238,-0.17366c-0.01109,-0.02554 -0.02282,-0.05077 -0.03422,-0.07618c-0.02155,-0.04792 -0.04335,-0.09564 -0.06604,-0.14305c-0.01325,-0.02777 -0.02694,-0.05533 -0.04056,-0.0829c-0.02225,-0.045 -0.045,-0.08981 -0.06827,-0.13437c-0.01457,-0.02789 -0.02922,-0.05564 -0.04417,-0.08335c-0.02409,-0.04455 -0.04881,-0.0888 -0.0739,-0.13291c-0.01509,-0.02649 -0.02998,-0.05299 -0.04539,-0.07929c-0.02789,-0.0476 -0.05672,-0.09469 -0.08588,-0.14172c-0.01357,-0.02193 -0.02674,-0.04405 -0.04056,-0.06579c-0.04342,-0.06839 -0.08791,-0.13626 -0.13392,-0.20339c-0.04285,-0.06249 -0.08689,-0.12385 -0.13152,-0.18469c-0.01292,-0.01762 -0.02611,-0.03505 -0.03917,-0.05254c-0.03429,-0.04582 -0.06908,-0.09108 -0.10439,-0.13595c-0.01274,-0.01616 -0.02535,-0.03251 -0.03828,-0.04855c-0.04684,-0.05844 -0.09444,-0.11624 -0.14305,-0.17296c-0.00634,-0.00735 -0.01287,-0.01451 -0.01921,-0.02186c-0.04284,-0.04963 -0.08645,-0.09856 -0.13062,-0.14685c-0.01522,-0.0166 -0.03049,-0.03302 -0.04582,-0.0495c-0.03917,-0.04202 -0.07891,-0.08353 -0.11909,-0.12447c-0.01325,-0.0135 -0.02624,-0.02713 -0.03961,-0.0405c-0.05229,-0.05248 -0.1054,-0.1042 -0.15928,-0.15491c-0.01109,-0.01046 -0.0225,-0.0206 -0.03365,-0.03099c-0.04405,-0.04088 -0.08854,-0.08113 -0.13354,-0.12081c-0.01711,-0.01508 -0.03435,-0.03004 -0.05159,-0.045c-0.04488,-0.03872 -0.09019,-0.07682 -0.13602,-0.11434c-0.01249,-0.01021 -0.02472,-0.02067 -0.03727,-0.0308c-0.05742,-0.04639 -0.11567,-0.09177 -0.17455,-0.13627c-0.0147,-0.01109 -0.02966,-0.02186 -0.04443,-0.03283c-0.04614,-0.03422 -0.0926,-0.06794 -0.13963,-0.10103c-0.01844,-0.01299 -0.03689,-0.02592 -0.0554,-0.03872c-0.05248,-0.03619 -0.1054,-0.07169 -0.15889,-0.10642c-0.00938,-0.00608 -0.01857,-0.01242 -0.02796,-0.01844c-0.05399,-0.03467 -0.10838,-0.0687 -0.16339,-0.10191c0.0142,-0.0135 0.02751,-0.02789 0.04151,-0.04158c0.0393,-0.03803 0.0779,-0.07669 0.11592,-0.11598c0.02124,-0.02193 0.04234,-0.04393 0.06313,-0.06623c0.04151,-0.04436 0.08195,-0.08962 0.12188,-0.13545c0.01699,-0.01945 0.03403,-0.03879 0.0507,-0.05856c0.04253,-0.05032 0.08385,-0.10172 0.12448,-0.15376c0.0133,-0.01699 0.02693,-0.03372 0.03999,-0.0509c0.04075,-0.05355 0.08011,-0.10819 0.11883,-0.16333c0.24731,0.03632 0.49652,0.0547 0.74498,0.0547c0.85911,0 1.66899,-0.21308 2.38132,-0.5876c1.95559,0.92649 3.20374,2.87575 3.20374,5.04703l0,1.58919l-3.70545,0z"
          />
        </g>
      </g>
    </g>
  </svg>
);
const portfolio: React.ReactNode = (
  <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" version="1.1">
    <g>
      <title>Layer 1</title>
      <g stroke-width="0" id="svg_1" stroke="null">
        <g id="svg_2" stroke="null" xmlns="http://www.w3.org/2000/svg">
          <g id="svg_3" stroke="null">
            <path
              id="svg_4"
              fill="currentColor"
              d="m21.21588,13.2294c-0.27273,0 -0.45453,0.21588 -0.45453,0.53964l0,7.01553c0,0.3238 -0.18184,0.53964 -0.45453,0.53964l-16.36367,0c-0.27273,0 -0.45453,-0.21588 -0.45453,-0.53964l0,-7.01553c0,-0.3238 -0.18184,-0.53964 -0.45453,-0.53964s-0.45453,0.21588 -0.45453,0.53964l0,7.01553c0,0.91742 0.5909,1.61896 1.36363,1.61896l16.36363,0c0.77273,0 1.36363,-0.70154 1.36363,-1.61896l0,-7.01553c0,-0.3238 -0.18184,-0.53964 -0.45457,-0.53964z"
              stroke="none"
            />
          </g>
        </g>
        <g id="svg_5" stroke="null" xmlns="http://www.w3.org/2000/svg">
          <g id="svg_6" stroke="null">
            <path
              id="svg_7"
              fill="currentColor"
              d="m20.76135,5.67423l-17.27274,0c-0.77273,0 -1.36363,0.70154 -1.36363,1.61896l0,3.45381c0,0.75548 0.45453,1.40308 1.04547,1.56498l7.13637,1.88878l0,1.72688c0,0.3238 0.18184,0.53964 0.45453,0.53964l2.72727,0c0.27273,0 0.45453,-0.21588 0.45453,-0.53964l0,-1.72688l7.13637,-1.88878c0.59094,-0.1619 1.04547,-0.8095 1.04547,-1.56503l0,-3.45381c0,-0.91738 -0.5909,-1.61892 -1.36363,-1.61892l-0.00001,0.00001zm-7.72727,9.71377l-1.81816,0l0,-2.1586l1.81816,0l0,2.1586zm8.1818,-4.64105c0,0.26982 -0.13637,0.4857 -0.36363,0.53964l-6.9091,1.83484l0,-0.43172c0,-0.3238 -0.18184,-0.53964 -0.45453,-0.53964l-2.72727,0c-0.27273,0 -0.45453,0.21588 -0.45453,0.53964l0,0.43172l-6.9091,-1.8348c-0.22727,-0.05398 -0.36363,-0.26982 -0.36363,-0.53964l0,-3.45381c0,-0.3238 0.18184,-0.53964 0.45453,-0.53964l17.27274,0c0.27273,0 0.45453,0.21588 0.45453,0.53964l0,3.45376l-0.00001,0.00001z"
              stroke="none"
            />
          </g>
        </g>
        <g id="svg_8" stroke="null" xmlns="http://www.w3.org/2000/svg">
          <g id="svg_9" stroke="null">
            <path
              id="svg_10"
              fill="currentColor"
              d="m14.39771,1.89662l-4.54547,0c-0.77273,0 -1.36363,0.70154 -1.36363,1.61896l0,0.53964c0,0.3238 0.18184,0.53964 0.45453,0.53964s0.45453,-0.21588 0.45453,-0.53964l0,-0.53964c0,-0.3238 0.18184,-0.53964 0.45453,-0.53964l4.54547,0c0.27273,0 0.45453,0.21588 0.45453,0.53964l0,0.53964c0,0.3238 0.18184,0.53964 0.45453,0.53964c0.27273,0 0.45453,-0.21588 0.45453,-0.53964l0,-0.53964c0.00008,-0.91742 -0.59082,-1.61896 -1.36355,-1.61896z"
              stroke="#fff"
            />
          </g>
        </g>
      </g>
    </g>
  </svg>
);

const crosshairs: React.ReactNode = (
  <svg id="Layer_1" className="close-btn" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">
    <title>close-sm</title>
    <path
      className="cls-1"
      d="M46.77,36l23-23A7.62,7.62,0,0,0,59,2.24l-23,23-23-23A7.62,7.62,0,0,0,2.23,13l23,23-23,23A7.62,7.62,0,0,0,13,69.78l23-23,23,23A7.62,7.62,0,0,0,69.77,59Z"
    />
  </svg>
);

const crosshairsCircle: React.ReactNode = (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
    <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm121.6 313.1c4.7 4.7 4.7 12.3 0 17L338 377.6c-4.7 4.7-12.3 4.7-17 0L256 312l-65.1 65.6c-4.7 4.7-12.3 4.7-17 0L134.4 338c-4.7-4.7-4.7-12.3 0-17l65.6-65-65.6-65.1c-4.7-4.7-4.7-12.3 0-17l39.6-39.6c4.7-4.7 12.3-4.7 17 0l65 65.7 65.1-65.6c4.7-4.7 12.3-4.7 17 0l39.6 39.6c4.7 4.7 4.7 12.3 0 17L312 256l65.6 65.1z" />
  </svg>
);

const crosshairsThin: React.ReactNode = (
  <svg
    version="1.1"
    id="Capa_1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    x="0px"
    y="0px"
    viewBox="0 0 511.76 511.76"
    xmlSpace="preserve"
  >
    <g>
      <g>
        <path
          d="M436.896,74.869c-99.84-99.819-262.208-99.819-362.048,0c-99.797,99.819-99.797,262.229,0,362.048
			c49.92,49.899,115.477,74.837,181.035,74.837s131.093-24.939,181.013-74.837C536.715,337.099,536.715,174.688,436.896,74.869z
			 M361.461,331.317c8.341,8.341,8.341,21.824,0,30.165c-4.16,4.16-9.621,6.251-15.083,6.251c-5.461,0-10.923-2.091-15.083-6.251
			l-75.413-75.435l-75.392,75.413c-4.181,4.16-9.643,6.251-15.083,6.251c-5.461,0-10.923-2.091-15.083-6.251
			c-8.341-8.341-8.341-21.845,0-30.165l75.392-75.413l-75.413-75.413c-8.341-8.341-8.341-21.845,0-30.165
			c8.32-8.341,21.824-8.341,30.165,0l75.413,75.413l75.413-75.413c8.341-8.341,21.824-8.341,30.165,0
			c8.341,8.32,8.341,21.824,0,30.165l-75.413,75.413L361.461,331.317z"
        />
      </g>
    </g>
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
  </svg>
);

const crosshairsThinAlt: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" height="329pt" viewBox="0 0 329.26933 329" width="329pt">
    <path d="m194.800781 164.769531 128.210938-128.214843c8.34375-8.339844 8.34375-21.824219 0-30.164063-8.339844-8.339844-21.824219-8.339844-30.164063 0l-128.214844 128.214844-128.210937-128.214844c-8.34375-8.339844-21.824219-8.339844-30.164063 0-8.34375 8.339844-8.34375 21.824219 0 30.164063l128.210938 128.214843-128.210938 128.214844c-8.34375 8.339844-8.34375 21.824219 0 30.164063 4.15625 4.160156 9.621094 6.25 15.082032 6.25 5.460937 0 10.921875-2.089844 15.082031-6.25l128.210937-128.214844 128.214844 128.214844c4.160156 4.160156 9.621094 6.25 15.082032 6.25 5.460937 0 10.921874-2.089844 15.082031-6.25 8.34375-8.339844 8.34375-21.824219 0-30.164063zm0 0" />
  </svg>
);

const dash: React.ReactNode = (
  <svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 42 42">
    <rect y="19" width="42" height="4" />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
  </svg>
);

const dna: React.ReactNode = (
  <svg id="dna-icon" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">
    <title>dna-off</title>
    <path
      className="dna-1"
      d="M22.13,7c-.76,1.31-1.47,2.66-2.16,4,0,.07-.07.13-.1.2a16.34,16.34,0,0,0-4.5-5.21l-.23-.15a10.1,10.1,0,0,0-3-1.27,10.84,10.84,0,0,0-4.71-.12A11.68,11.68,0,0,0,4.23,5.55c-.33.18-.65.38-1,.59A2.08,2.08,0,1,1,.92,2.69,16,16,0,0,1,4.22,1,15.6,15.6,0,0,1,7.43.15C8,.08,8.5,0,9.05,0a14.8,14.8,0,0,1,6.31,1.18,15.24,15.24,0,0,1,2,1.05A17.14,17.14,0,0,1,22.13,7Z"
    />
    <path
      className="dna-1"
      d="M71.6,5.39A2.08,2.08,0,0,1,68.7,6a10.91,10.91,0,0,0-1.51-.84A8.75,8.75,0,0,0,64,4.3a10.05,10.05,0,0,0-4.71.91,15.59,15.59,0,0,0-3.21,1.93c-.25.19-.48.38-.7.57a15.32,15.32,0,0,0-2.88,3.41c-.8,1.23-1.52,2.57-2.25,3.94a46.4,46.4,0,0,1-3.06,5.19,20.38,20.38,0,0,1-1.56,1.94,17.75,17.75,0,0,1-4.06,3.38A13.48,13.48,0,0,1,38.34,27a10.41,10.41,0,0,1-2.81.39h-.15a9.36,9.36,0,0,1-1.75-.18A10.47,10.47,0,0,1,30.42,26a13.25,13.25,0,0,1-3.36-2.85,21.91,21.91,0,0,1-1.9-2.57c.85-1.34,1.61-2.76,2.36-4.17a24.36,24.36,0,0,0,2.7,4l.18.21a8.06,8.06,0,0,0,3.22,2.27,5.67,5.67,0,0,0,1.81.33,6.91,6.91,0,0,0,2.9-.65,11.71,11.71,0,0,0,3.21-2.18q.49-.47,1-1a19.9,19.9,0,0,0,2.26-3.12c.6-1,1.17-2.07,1.76-3.16a39.38,39.38,0,0,1,3.65-6,17,17,0,0,1,2.45-2.59,20.23,20.23,0,0,1,3.4-2.37A16.94,16.94,0,0,1,59.26.77,14.24,14.24,0,0,1,61.34.28,13,13,0,0,1,64,.13a12.62,12.62,0,0,1,3.21.54A13.89,13.89,0,0,1,71,2.49,2.09,2.09,0,0,1,71.6,5.39Z"
    />
    <path
      className="dna-1"
      d="M45.56,5.14a20.81,20.81,0,0,1,1.64,2,46.09,46.09,0,0,0-2.36,4A20.66,20.66,0,0,0,42.5,8c-2.29-2.48-4.82-3.82-7.12-3.78A7.06,7.06,0,0,0,30.18,7a37.93,37.93,0,0,0-4.27,7c-2.24,4.28-4.55,8.71-8.49,11.21A14.61,14.61,0,0,1,9.6,27.53H9.12A15.49,15.49,0,0,1,1,24.89,2.09,2.09,0,0,1,3.3,21.42a10.88,10.88,0,0,0,11.89.3c3-1.89,5-5.82,7-9.61A42.45,42.45,0,0,1,27,4.31,11.15,11.15,0,0,1,35.3,0h.16C38.92,0,42.5,1.82,45.56,5.14Z"
    />
    <path
      className="dna-1"
      d="M71.08,24.7A13.37,13.37,0,0,1,61.42,27a17.89,17.89,0,0,1-8.72-4.2,17.67,17.67,0,0,1-2.54-2.66c.81-1.3,1.51-2.62,2.2-3.9l.06-.13a15.52,15.52,0,0,0,3,3.5C58,21.79,63.26,25,68.74,21.24a2.09,2.09,0,0,1,2.34,3.46Z"
    />
    <path
      className="dna-2"
      d="M5.43,7.76a9.41,9.41,0,0,1,2-.79l0,13.6A8.81,8.81,0,0,1,4.7,19.33a3.7,3.7,0,0,0-.43-.25l0-10.6a3.7,3.7,0,0,0,.43-.25A8.65,8.65,0,0,1,5.43,7.76Z"
    />
    <path
      className="dna-2"
      d="M15.38,9.24l0,9a8,8,0,0,1-1.56,1.34,8,8,0,0,1-1.64.79l0-13.26a7.48,7.48,0,0,1,1.64.78h0L14,8A8.61,8.61,0,0,1,15.38,9.24Z"
    />
    <path
      className="dna-2"
      d="M33.58,7.32l0,12.89A7.12,7.12,0,0,1,32.18,19l-.13-.15h0a18.51,18.51,0,0,1-1.65-2.3l0-5.41a19.78,19.78,0,0,1,1.72-2.45A7,7,0,0,1,33.58,7.32Z"
    />
    <path
      className="dna-2"
      d="M41.51,10.73l0,6.08c-.26.34-.52.65-.78.94s-.57.6-.85.86a10.59,10.59,0,0,1-1.58,1.23l0-12.17a11.72,11.72,0,0,1,2.35,2A12.76,12.76,0,0,1,41.51,10.73Z"
    />
    <path
      className="dna-2"
      d="M57.53,9.05a13.94,13.94,0,0,1,1.75-1.16l0,11.36A14.78,14.78,0,0,1,57,17.65a9.88,9.88,0,0,1-.93-.9l0-6.38a9.43,9.43,0,0,1,.85-.83Z"
    />
    <path
      className="dna-2"
      d="M66.19,7.31a7.68,7.68,0,0,1,1,.55l0,11.37A6.93,6.93,0,0,1,64,20.41l0-13.7A6.8,6.8,0,0,1,66.19,7.31Z"
    />
  </svg>
);

const dots: React.ReactNode = (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="more-menu">
    <path d="M328 256c0 39.8-32.2 72-72 72s-72-32.2-72-72 32.2-72 72-72 72 32.2 72 72zm104-72c-39.8 0-72 32.2-72 72s32.2 72 72 72 72-32.2 72-72-32.2-72-72-72zm-352 0c-39.8 0-72 32.2-72 72s32.2 72 72 72 72-32.2 72-72-32.2-72-72-72z" />
  </svg>
);

const dotsThin: React.ReactNode = (
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 20">
    <circle cx="14" cy="8" r="2" />
    <circle cx="8" cy="8" r="2" />
    <circle cx="2" cy="8" r="2" />
  </svg>
);

const editSquare: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 121 121.3">
    <g>
      <path
        d="M69.3,8.7c0,2.8,0,5.5,0,8.3c-0.4,0-0.7,0.1-1,0.1c-19.6,0-39.2,0-58.8,0c-1,0-1,0-1,1.1c0,31.1,0,62.3,0,93.4
      c0,0.3,0,0.6,0,1c31.9,0,63.7,0,95.6,0c0-20.2,0-40.3,0-60.5c2.9,0,5.8,0,8.7,0c0,0.3,0,0.6,0,0.9c0,19.7,0,39.5,0,59.2
      c0,5.4-3.7,9.1-9.1,9.1c-31.5,0-63,0-94.5,0c-5.4,0-9.1-3.7-9.1-9.1c0-31.5,0-62.9,0-94.4c0-5.4,3.7-9.1,9.1-9.1
      c19.8,0,39.5,0,59.3,0C68.7,8.7,68.9,8.7,69.3,8.7z"
      />
      <path
        d="M63.5,75.7c-6-6.1-12.1-12.2-18.2-18.2C59.4,43.3,73.6,29.1,87.7,15c6,6.1,12.1,12.2,18.1,18.2
      C91.8,47.3,77.6,61.5,63.5,75.7z"
      />
      <path
        d="M94.2,8.2c0.3-0.3,0.8-0.7,1.3-1.1c1.6-1.5,3.1-3.1,4.7-4.6c3.3-3.2,8.5-3.3,11.9-0.1c2.3,2.1,4.5,4.4,6.6,6.6
      c3.1,3.3,3,8.5-0.1,11.7c-2,2.1-4,4-6,6C106.5,20.6,100.4,14.5,94.2,8.2z"
      />
      <path
        d="M57.3,82.5c-1.3,0.6-2.6,1.6-3.9,1.7c-5.6,0.7-10.7,3.1-16.1,4.6c-2.4,0.7-4.8,1.5-7.4,2.3c0.1-0.4,0.2-0.6,0.2-0.9
      c2-6.4,4.1-12.8,6.1-19.2c0.1-0.4,0.2-0.8,0.2-1.2c0-1.9,0.6-3.7,1.7-5.2c0.2-0.3,0.3-0.5,0.5-0.8C44.8,70,51,76.1,57.3,82.5z"
      />
    </g>
  </svg>
);

const emptyInbox: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 416.03">
    <title>Empty Inbox</title>
    <g id="Layer_2" data-name="Layer 2">
      <g id="Layer_1-2" data-name="Layer 1">
        <path d="M512,334c-3.23,5.6-8,7.32-14.47,7.3-57-.23-114-.13-170.94-.13H320c0,2.15,0,3.91,0,5.67,0,19.16.07,38.32,0,57.48-.05,9.08-8.29,14.41-15.69,10.29-4-2.24-5.52-5.82-5.49-10.43.11-18.83,0-37.65,0-56.48,0-1.82,0-3.64,0-5.89H213.41c-.08,1.8-.23,3.56-.23,5.32q0,28.24,0,56.48c0,7.58-4.24,12.45-10.63,12.37S192,410.92,192,403.39c0-20.48,0-41,0-62.22h-6c-57.15,0-114.29-.09-171.44.12C8.06,341.32,3.2,339.72,0,334Q0,225,0,116a14.4,14.4,0,0,0,.89-2.74A128.05,128.05,0,0,1,128.47,0Q250.91,0,373.35,0a144.46,144.46,0,0,1,20.43,1c52.36,7.54,89.63,35.25,110,84.31,4.14,10,5.54,21.09,8.21,31.68ZM199.13,21.17c1.94,1.61,2.88,2.46,3.88,3.22,34.71,26.26,52.71,61.13,52.91,104.68.28,61.66.07,123.33.07,185v5.58H490.65c.07-1.33.17-2.31.17-3.29q0-93.49,0-187c0-15.51-2.89-30.59-10.11-44.4C458.51,42.6,422.88,21.7,375.52,21.29c-56.83-.5-113.66-.12-170.49-.12Zm35.7,298.64v-6.5q0-86.71,0-173.41c0-5.66-.08-11.32-.35-17-3.11-65.8-64.94-113.25-129.07-99.09-50,11-84,53.75-84.18,106.19-.19,61.8,0,123.6,0,185.41,0,1.44.19,2.87.29,4.38Z" />
        <path d="M405.16,128h-6q-49.49,0-99,0c-6.89,0-11.46-3.6-12.14-9.38a10.52,10.52,0,0,1,9.12-11.7,41.06,41.06,0,0,1,5-.15q58.24-.13,116.47-.23c13.16,0,26.32.17,39.48.33,7.1.09,11,4,11,11.27q.07,42,0,84c0,7.1-4,11-11.26,11q-20.25.09-40.49,0c-8.13,0-12.06-3.93-12.18-12.43-.15-10.5,0-21,0-31.5C405.16,155.74,405.16,142.25,405.16,128Zm22,.17v63.51h20.51V128.18Z" />
        <path d="M182.37,229.15c-15.38,0-29-12.08-30.53-27.06-.35-3.46-1.07-7.52,3.75-8.21s5.07,3.4,5.62,6.85c1.72,10.87,9.29,18.15,19.4,18.77,10.35.63,19.1-5.26,22-14.91a33.25,33.25,0,0,0,.89-5.34c.41-3.22,1.71-5.85,5.4-5.42s4.24,3.3,4.15,6.51C212.59,215.63,198.23,229.15,182.37,229.15Z" />
        <path d="M74.25,229.12c-15.2,0-28.67-11.86-30.39-26.69-.42-3.66-1.21-8,3.94-8.57,4.85-.53,4.88,3.77,5.41,7.2,1.51,9.83,8.4,17,17.52,18.34a21.45,21.45,0,0,0,22.65-11.6,24.41,24.41,0,0,0,2.19-8.91c.31-3,1.8-5.38,5.18-5s4.21,2.79,4.22,5.92C105.06,215.09,90.39,229.16,74.25,229.12Z" />
        <path d="M126.8,288.25c-8.18,0-16.37-.06-24.55,0-3.53,0-6.47-.9-6.42-4.87,0-3.64,2.89-4.63,6.16-4.62q25.37,0,50.73,0c3.27,0,6.12,1,6.16,4.63,0,4-2.93,4.89-6.44,4.86C143.89,288.19,135.34,288.25,126.8,288.25Z" />
      </g>
    </g>
  </svg>
);

const eyeClosed: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M19.604 2.562l-3.346 3.137c-1.27-.428-2.686-.699-4.243-.699-7.569 0-12.015 6.551-12.015 6.551s1.928 2.951 5.146 5.138l-2.911 2.909 1.414 1.414 17.37-17.035-1.415-1.415zm-6.016 5.779c-3.288-1.453-6.681 1.908-5.265 5.206l-1.726 1.707c-1.814-1.16-3.225-2.65-4.06-3.66 1.493-1.648 4.817-4.594 9.478-4.594.927 0 1.796.119 2.61.315l-1.037 1.026zm-2.883 7.431l5.09-4.993c1.017 3.111-2.003 6.067-5.09 4.993zm13.295-4.221s-4.252 7.449-11.985 7.449c-1.379 0-2.662-.291-3.851-.737l1.614-1.583c.715.193 1.458.32 2.237.32 4.791 0 8.104-3.527 9.504-5.364-.729-.822-1.956-1.99-3.587-2.952l1.489-1.46c2.982 1.9 4.579 4.327 4.579 4.327z" />
  </svg>
);

const eyeOpened: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M12.015 7c4.751 0 8.063 3.012 9.504 4.636-1.401 1.837-4.713 5.364-9.504 5.364-4.42 0-7.93-3.536-9.478-5.407 1.493-1.647 4.817-4.593 9.478-4.593zm0-2c-7.569 0-12.015 6.551-12.015 6.551s4.835 7.449 12.015 7.449c7.733 0 11.985-7.449 11.985-7.449s-4.291-6.551-11.985-6.551zm-.015 5c1.103 0 2 .897 2 2s-.897 2-2 2-2-.897-2-2 .897-2 2-2zm0-2c-2.209 0-4 1.792-4 4 0 2.209 1.791 4 4 4s4-1.791 4-4c0-2.208-1.791-4-4-4z" />
  </svg>
);

const externalLink: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 87.7 87.9">
    <title>External_link_icon</title>
    <path d="M0,87.9V86C0,65.4.1,44.7,0,24c0-1.7.5-2.1,2.1-2.1H61l7.3-7.1C63,10,58.1,5.2,53.1.5l.4-.5H87.7V34.5c-4.5-4.5-9.1-8.1-13.8-12.8L66,29.8a3.1,3.1,0,0,0-.4,2V87.9Zm58.2-7.3V36.9L36.1,58.7c-4.4-4.2-1.7-1.3-6.5-6L53.4,29.5H7.5c0,.6-.1,1.2-.1,1.8V78.7c0,1.5.4,2.1,2,2H58.2Z" />
  </svg>
);

const externalLinkAlt: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 916.9 916.61">
    <path d="M826.4,497.31v224.8c0,16,1,32.4-.7,48.3l-.3,2.7c.5-3.9,1.1-7.7,1.6-11.6a118.9,118.9,0,0,1-7.8,28.6c1.5-3.5,3-7.1,4.4-10.6a119.82,119.82,0,0,1-14.5,25c2.3-3.1,4.7-6.1,7-9.2a118.2,118.2,0,0,1-20.2,20.4c3.1-2.4,6.1-4.8,9.2-7.2a117.57,117.57,0,0,1-26.1,15.3c3.7-1.5,7.4-3.1,11.1-4.6a117.66,117.66,0,0,1-28.6,7.8l12-1.5c-10.5,1.3-21.1,1-31.7,1H183.7c-13.4,0-27.1.7-40.4-1,3.9.5,7.7,1.1,11.6,1.6a118.9,118.9,0,0,1-28.6-7.8c3.5,1.5,7.1,3,10.6,4.4a119.82,119.82,0,0,1-25-14.5c3.1,2.3,6.1,4.7,9.2,7A118.19,118.19,0,0,1,100.7,796c2.4,3.1,4.8,6.1,7.2,9.2a117.57,117.57,0,0,1-15.3-26.1c1.6,3.7,3.1,7.4,4.6,11.1a117.66,117.66,0,0,1-7.8-28.6l1.5,12c-1.3-10.5-1-21.1-1-31.7V183.81c0-13.4-.7-27.1,1-40.4-.5,3.9-1.1,7.7-1.6,11.6a118.9,118.9,0,0,1,7.8-28.6c-1.5,3.5-3,7.1-4.4,10.6a119.82,119.82,0,0,1,14.5-25c-2.3,3.1-4.7,6.1-7,9.2a118.19,118.19,0,0,1,20.2-20.4c-3.1,2.4-6.1,4.8-9.2,7.2a117.57,117.57,0,0,1,26.1-15.3c-3.7,1.6-7.4,3.1-11.1,4.6a117.66,117.66,0,0,1,28.6-7.8l-12,1.5c16.3-2.1,33.4-1,49.8-1H416.2c24.5,0,45.2-20,45.3-44.7C461.7,20.91,441.3,0,416.8,0H192.5c-20.6,0-40.6-.3-61.1,2.6C102,6.71,75.9,21.31,53.4,40,31.3,58.41,16,84.51,7.1,111.51.9,130.21,0,149.91,0,169.31V756c0,44,17.6,87.1,49.9,117.3,30,28.3,69.6,43.3,110.7,43.3H748.5c22.1,0,44.4-1.8,65-10.3,8.3-3.4,16.4-6.7,24.2-11.1s15.1-10.3,22.3-16c11.4-9.1,20.1-20,28.8-31.7,17.4-23.5,25.6-53.1,27.6-81.9,1-15.1.2-30.5.2-45.7V498c0-24.5-20-45.2-44.7-45.3s-45.5,20.1-45.5,44.6Z" />
    <path d="M582.3,90.21q63.75,0,127.4.1,78.15,0,156.2.1h5c24.5,0,45.2-20.1,45.3-44.7.2-24.4-20.2-45.3-44.7-45.3q-63.75,0-127.4-.1-78.15,0-156.2-.1h-5c-24.5,0-45.2,20.1-45.3,44.7-.1,24.4,20.2,45.3,44.7,45.3Z" />
    <path d="M916.1,334.21q0-63.75.1-127.4,0-78.15.1-156.2v-5c0-24.5-20.1-45.2-44.7-45.3-24.4-.2-45.3,20.2-45.3,44.7q0,63.75-.1,127.4,0,78.15-.1,156.2v5c0,24.5,20.1,45.2,44.7,45.3,24.4.2,45.3-20.1,45.3-44.7Z" />
    <path d="M435.1,544.81l49.3-49.3Q543,436.86,601.8,378.21q69.75-69.75,139.6-139.4,57.9-57.9,115.8-115.7l46.2-46.2c17.3-17.3,17-46.5-.3-63.6-17.5-17.3-46.3-17-63.6.3l-49.3,49.3q-58.65,58.65-117.4,117.3Q603,250,533.2,319.61q-57.9,57.9-115.8,115.7l-46.2,46.2c-17.3,17.3-17,46.5.3,63.6,17.4,17.3,46.2,17,63.6-.3Z" />
  </svg>
);

const medium: React.ReactNode = (
  <svg viewBox="0 0 1043.63 592.71">
    <g>
      <g>
        <path
          d="M588.67 296.36c0 163.67-131.78 296.35-294.33 296.35S0 460 0
    296.36 131.78 0 294.34 0s294.33 132.69 294.33 296.36M911.56 296.36c0
    154.06-65.89 279-147.17 279s-147.17-124.94-147.17-279 65.88-279
    147.16-279 147.17 124.9 147.17 279M1043.63 296.36c0 138-23.17 249.94-51.76
    249.94s-51.75-111.91-51.75-249.94 23.17-249.94 51.75-249.94 51.76 111.9
    51.76 249.94"
        ></path>
      </g>
    </g>
  </svg>
);

const currency: React.ReactNode = (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 288 512"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M209.2 233.4l-108-31.6C88.7 198.2 80 186.5 80 173.5c0-16.3 13.2-29.5 29.5-29.5h66.3c12.2 0 24.2 3.7 34.2 10.5 6.1 4.1 14.3 3.1 19.5-2l34.8-34c7.1-6.9 6.1-18.4-1.8-24.5C238 74.8 207.4 64.1 176 64V16c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v48h-2.5C45.8 64-5.4 118.7.5 183.6c4.2 46.1 39.4 83.6 83.8 96.6l102.5 30c12.5 3.7 21.2 15.3 21.2 28.3 0 16.3-13.2 29.5-29.5 29.5h-66.3C100 368 88 364.3 78 357.5c-6.1-4.1-14.3-3.1-19.5 2l-34.8 34c-7.1 6.9-6.1 18.4 1.8 24.5 24.5 19.2 55.1 29.9 86.5 30v48c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16v-48.2c46.6-.9 90.3-28.6 105.7-72.7 21.5-61.6-14.6-124.8-72.5-141.7z"></path>
  </svg>
);

const tumblr: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path
      d="M19.512 17.489l-.096-.068h-3.274c-.153 0-.16-.467-.163-.622v-5.714c0-.056.045-.101.101-.101h3.82c.056 0 .101-.045.101-.101v-5.766c0-.055-.045-.1-.101-.1h-3.803c-.055 0-.1-.045-.1-.101v-4.816c0-.055-.045-.1-.101-.1h-7.15c-.489 0-1.053.362-1.135 1.034-.341 2.778-1.882 4.125-4.276 4.925l-.267.089-.068.096v4.74c0 .056.045.101.1.101h2.9v6.156c0 4.66 3.04 6.859 9.008 6.859 2.401 0 5.048-.855 5.835-1.891l.157-.208-1.488-4.412zm.339 4.258c-.75.721-2.554 1.256-4.028 1.281l-.165.001c-4.849 0-5.682-3.701-5.682-5.889v-7.039c0-.056-.045-.101-.1-.101h-2.782c-.056 0-.101-.045-.101-.101l-.024-3.06.064-.092c2.506-.976 3.905-2.595 4.273-5.593.021-.167.158-.171.159-.171h3.447c.055 0 .101.045.101.101v4.816c0 .056.045.101.1.101h3.803c.056 0 .101.045.101.1v3.801c0 .056-.045.101-.101.101h-3.819c-.056 0-.097.045-.097.101v6.705c.023 1.438.715 2.167 2.065 2.167.544 0 1.116-.126 1.685-.344.053-.021.111.007.13.061l.995 2.95-.024.104z"
      fillRule="evenodd"
      clipRule="evenodd"
    />
  </svg>
);

const youtube: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M4.652 0h1.44l.988 3.702.916-3.702h1.454l-1.665 5.505v3.757h-1.431v-3.757l-1.702-5.505zm6.594 2.373c-1.119 0-1.861.74-1.861 1.835v3.349c0 1.204.629 1.831 1.861 1.831 1.022 0 1.826-.683 1.826-1.831v-3.349c0-1.069-.797-1.835-1.826-1.835zm.531 5.127c0 .372-.19.646-.532.646-.351 0-.554-.287-.554-.646v-3.179c0-.374.172-.651.529-.651.39 0 .557.269.557.651v3.179zm4.729-5.07v5.186c-.155.194-.5.512-.747.512-.271 0-.338-.186-.338-.46v-5.238h-1.27v5.71c0 .675.206 1.22.887 1.22.384 0 .918-.2 1.468-.853v.754h1.27v-6.831h-1.27zm2.203 13.858c-.448 0-.541.315-.541.763v.659h1.069v-.66c.001-.44-.092-.762-.528-.762zm-4.703.04c-.084.043-.167.109-.25.198v4.055c.099.106.194.182.287.229.197.1.485.107.619-.067.07-.092.105-.241.105-.449v-3.359c0-.22-.043-.386-.129-.5-.147-.193-.42-.214-.632-.107zm4.827-5.195c-2.604-.177-11.066-.177-13.666 0-2.814.192-3.146 1.892-3.167 6.367.021 4.467.35 6.175 3.167 6.367 2.6.177 11.062.177 13.666 0 2.814-.192 3.146-1.893 3.167-6.367-.021-4.467-.35-6.175-3.167-6.367zm-12.324 10.686h-1.363v-7.54h-1.41v-1.28h4.182v1.28h-1.41v7.54zm4.846 0h-1.21v-.718c-.223.265-.455.467-.696.605-.652.374-1.547.365-1.547-.955v-5.438h1.209v4.988c0 .262.063.438.322.438.236 0 .564-.303.711-.487v-4.939h1.21v6.506zm4.657-1.348c0 .805-.301 1.431-1.106 1.431-.443 0-.812-.162-1.149-.583v.5h-1.221v-8.82h1.221v2.84c.273-.333.644-.608 1.076-.608.886 0 1.18.749 1.18 1.631v3.609zm4.471-1.752h-2.314v1.228c0 .488.042.91.528.91.511 0 .541-.344.541-.91v-.452h1.245v.489c0 1.253-.538 2.013-1.813 2.013-1.155 0-1.746-.842-1.746-2.013v-2.921c0-1.129.746-1.914 1.837-1.914 1.161 0 1.721.738 1.721 1.914v1.656z" />
  </svg>
);

const whatsapp: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
  </svg>
);

const other: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M6.188 8.719c.439-.439.926-.801 1.444-1.087 2.887-1.591 6.589-.745 8.445 2.069l-2.246 2.245c-.644-1.469-2.243-2.305-3.834-1.949-.599.134-1.168.433-1.633.898l-4.304 4.306c-1.307 1.307-1.307 3.433 0 4.74 1.307 1.307 3.433 1.307 4.74 0l1.327-1.327c1.207.479 2.501.67 3.779.575l-2.929 2.929c-2.511 2.511-6.582 2.511-9.093 0s-2.511-6.582 0-9.093l4.304-4.306zm6.836-6.836l-2.929 2.929c1.277-.096 2.572.096 3.779.574l1.326-1.326c1.307-1.307 3.433-1.307 4.74 0 1.307 1.307 1.307 3.433 0 4.74l-4.305 4.305c-1.311 1.311-3.44 1.3-4.74 0-.303-.303-.564-.68-.727-1.051l-2.246 2.245c.236.358.481.667.796.982.812.812 1.846 1.417 3.036 1.704 1.542.371 3.194.166 4.613-.617.518-.286 1.005-.648 1.444-1.087l4.304-4.305c2.512-2.511 2.512-6.582.001-9.093-2.511-2.51-6.581-2.51-9.092 0z" />
  </svg>
);

const globe: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm10 12c0 .685-.07 1.354-.202 2h-3.853c.121-1.283.129-2.621 0-4h3.853c.132.646.202 1.315.202 2zm-.841-4h-3.5c-.383-1.96-1.052-3.751-1.948-5.278 2.435.977 4.397 2.882 5.448 5.278zm-5.554 0h-2.605v-5.658c1.215 1.46 2.117 3.41 2.605 5.658zm-4.605-5.658v5.658h-2.605c.488-2.248 1.39-4.198 2.605-5.658zm0 7.658v4h-2.93c-.146-1.421-.146-2.577 0-4h2.93zm0 6v5.658c-1.215-1.46-2.117-3.41-2.605-5.658h2.605zm2 5.658v-5.658h2.605c-.488 2.248-1.39 4.198-2.605 5.658zm0-7.658v-4h2.93c.146 1.421.146 2.577 0 4h-2.93zm-4.711-11.278c-.896 1.527-1.565 3.318-1.948 5.278h-3.5c1.051-2.396 3.013-4.301 5.448-5.278zm-6.087 7.278h3.853c-.121 1.283-.129 2.621 0 4h-3.853c-.132-.646-.202-1.315-.202-2s.07-1.354.202-2zm.639 6h3.5c.383 1.96 1.052 3.751 1.948 5.278-2.435-.977-4.397-2.882-5.448-5.278zm12.87 5.278c.896-1.527 1.565-3.318 1.948-5.278h3.5c-1.051 2.396-3.013 4.301-5.448 5.278z" />
  </svg>
);

const weibo: React.ReactNode = (
  <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 512 512">
    <g>
      <path
        d="M220,411.6c-74.5,7.4-138.9-26.4-143.7-75.2c-4.8-49,51.7-94.5,126.1-101.9c74.5-7.4,138.9,26.4,143.6,75.2
		C351,358.7,294.5,404.3,220,411.6z M369,249.2c-6.3-1.9-10.7-3.2-7.4-11.5c7.2-18.1,7.9-33.7,0.1-44.8
		c-14.6-20.8-54.6-19.8-100.4-0.5c0,0-14.4,6.2-10.7-5.1c7-22.7,6-41.6-5-52.6c-24.9-24.9-91,1-147.7,57.7
		c-42.4,42.3-67,87.4-67,126.3c0,74.4,95.5,119.6,188.8,119.6c122.4,0,203.8-71.1,203.8-127.5C423.6,276.6,394.9,257.3,369,249.2
		L369,249.2z"
      />
      <path
        d="M450.3,113c-29.5-32.8-73.1-45.3-113.4-36.7l0,0c-9.3,2-15.2,11.2-13.2,20.4c2,9.3,11.1,15.2,20.4,13.2
		c28.7-6.1,59.6,2.8,80.6,26.1c21,23.3,26.7,55,17.7,82.8l0,0c-2.9,9.1,2,18.7,11.1,21.7c9.1,2.9,18.7-2,21.7-11.1v-0.1
		C487.9,190.5,479.9,145.8,450.3,113L450.3,113z"
      />
      <path
        d="M404.9,154.1c-14.4-16-35.7-22.1-55.2-17.8c-8,1.7-13.1,9.7-11.4,17.7c1.8,8,9.7,13.1,17.6,11.3l0,0c9.6-2,19.9,1,27,8.7
		c7,7.8,9,18.5,5.9,27.8l0,0c-2.5,7.7,1.8,16.2,9.6,18.7c7.8,2.5,16.2-1.8,18.7-9.6C423.2,191.8,419.3,170,404.9,154.1L404.9,154.1z
		"
      />
      <path
        d="M224.2,323.1c-2.6,4.5-8.4,6.6-12.8,4.8c-4.5-1.8-5.8-6.9-3.3-11.2c2.6-4.3,8.2-6.4,12.6-4.8
		C225,313.5,226.6,318.5,224.2,323.1z M200.4,353.5c-7.2,11.5-22.7,16.5-34.3,11.3c-11.4-5.2-14.9-18.5-7.7-29.8
		c7.1-11.2,22.1-16.2,33.6-11.3C203.7,328.6,207.5,341.9,200.4,353.5L200.4,353.5z M227.4,272.1c-35.4-9.2-75.5,8.4-90.9,39.6
		c-15.7,31.8-0.5,67.3,35.2,78.8c37.1,12,80.9-6.4,96.1-40.7C282.9,316.2,264.2,281.7,227.4,272.1L227.4,272.1z"
      />
    </g>
  </svg>
);
const snapchat: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M5.829 4.533c-.6 1.344-.363 3.752-.267 5.436-.648.359-1.48-.271-1.951-.271-.49 0-1.075.322-1.167.802-.066.346.089.85 1.201 1.289.43.17 1.453.37 1.69.928.333.784-1.71 4.403-4.918 4.931-.251.041-.43.265-.416.519.056.975 2.242 1.357 3.211 1.507.099.134.179.7.306 1.131.057.193.204.424.582.424.493 0 1.312-.38 2.738-.144 1.398.233 2.712 2.215 5.235 2.215 2.345 0 3.744-1.991 5.09-2.215.779-.129 1.448-.088 2.196.058.515.101.977.157 1.124-.349.129-.437.208-.992.305-1.123.96-.149 3.156-.53 3.211-1.505.014-.254-.165-.477-.416-.519-3.154-.52-5.259-4.128-4.918-4.931.236-.557 1.252-.755 1.69-.928.814-.321 1.222-.716 1.213-1.173-.011-.585-.715-.934-1.233-.934-.527 0-1.284.624-1.897.286.096-1.698.332-4.095-.267-5.438-1.135-2.543-3.66-3.829-6.184-3.829-2.508 0-5.014 1.268-6.158 3.833z" />
  </svg>
);

const angellist: React.ReactNode = (
  <svg
    version="1.1"
    id="Layer_1"
    xmlns="http://www.w3.org/2000/svg"
    x="32px"
    y="32px"
    viewBox="0 0 21.9 31.4"
    enable-background="new 0 0 21.9 31.4"
  >
    <g>
      <path d="M18.3,13.4c1.3,0.2,2.2,0.8,2.7,1.6c0.5,0.8,0.8,2.2,0.8,4c0,3.6-1.1,6.6-3.3,8.9c-2.2,2.3-5,3.5-8.4,3.5 c-1.3,0-2.6-0.2-3.9-0.7c-1.3-0.5-2.3-1.2-3.2-2c-1-0.9-1.8-2-2.3-3C0.3,24.6,0,23.5,0,22.4c0-1.2,0.3-2.2,0.8-2.9 c0.5-0.7,1.4-1.1,2.5-1.4c-0.2-0.5-0.4-0.9-0.5-1.3c-0.1-0.3-0.2-0.6-0.2-0.8c0-0.6,0.3-1.3,1-2c0.7-0.7,1.3-1,1.9-1 c0.3,0,0.5,0,0.8,0.1c0.3,0.1,0.6,0.2,1,0.5C6.4,10.6,5.5,8.1,5,6.4C4.5,4.7,4.3,3.6,4.3,2.9C4.3,2,4.5,1.3,5,0.8 C5.4,0.3,6.1,0,6.9,0C8.2,0,9.9,3,12,9.1c0.4,1,0.6,1.8,0.8,2.4c0.2-0.4,0.4-1.1,0.7-1.9c2.1-6,3.9-9,5.3-9c0.7,0,1.3,0.2,1.8,0.7 c0.4,0.5,0.7,1.2,0.7,2c0,0.6-0.2,1.8-0.7,3.5C20,8.5,19.3,10.7,18.3,13.4z M2.9,22.2c0.2,0.2,0.5,0.6,0.8,1.1c0.9,1.3,1.8,2,2.6,2 c0.3,0,0.5-0.1,0.7-0.3c0.2-0.2,0.3-0.4,0.3-0.5c0-0.2-0.1-0.6-0.4-1.1c-0.3-0.5-0.7-1.1-1.2-1.7c-0.6-0.7-1.1-1.3-1.4-1.6 c-0.4-0.3-0.7-0.5-0.9-0.5c-0.5,0-1,0.3-1.4,0.8c-0.4,0.5-0.6,1.2-0.6,1.9c0,0.6,0.1,1.2,0.4,2c0.3,0.7,0.7,1.5,1.3,2.2 C4,27.5,5,28.4,6.3,29c1.3,0.6,2.6,0.9,4.2,0.9c2.8,0,5.1-1,7-3.1c1.9-2.1,2.8-4.7,2.8-7.9c0-1-0.1-1.7-0.2-2.3 c-0.1-0.6-0.4-1-0.7-1.2c-0.6-0.5-1.7-0.9-3.4-1.3c-1.7-0.4-3.5-0.6-5.3-0.6c-0.5,0-0.9,0.1-1.1,0.3c-0.2,0.2-0.3,0.5-0.3,0.9 c0,1,0.5,1.7,1.6,2.1c1.1,0.4,2.9,0.7,5.3,0.7h0.9c0.2,0,0.4,0.1,0.5,0.2c0.1,0.2,0.2,0.4,0.2,0.7c-0.2,0.2-0.7,0.5-1.5,0.8 c-0.8,0.3-1.3,0.6-1.7,0.9c-0.9,0.6-1.6,1.4-2.1,2.3c-0.5,0.9-0.8,1.7-0.8,2.5c0,0.5,0.1,1,0.3,1.7c0.2,0.7,0.3,1.1,0.3,1.2v0.2 l0,0.2c-0.6,0-1.1-0.4-1.5-1.1c-0.4-0.7-0.5-1.6-0.5-2.8v-0.2c-0.1,0.1-0.2,0.2-0.3,0.2c-0.1,0-0.2,0.1-0.4,0.1c-0.1,0-0.3,0-0.4,0 c-0.1,0-0.2-0.1-0.4-0.1c0,0.2,0.1,0.3,0.1,0.5c0,0.2,0,0.3,0,0.4c0,0.5-0.2,1-0.6,1.4c-0.4,0.4-0.9,0.6-1.5,0.6 c-0.9,0-1.8-0.4-2.8-1.3c-0.9-0.9-1.4-1.7-1.4-2.6c0-0.2,0-0.3,0.1-0.4C2.7,22.4,2.8,22.3,2.9,22.2z M9.4,22.7 c0.2,0,0.5-0.1,0.7-0.3c0.2-0.2,0.3-0.5,0.3-0.7c0-0.3-0.2-0.9-0.6-1.9c-0.4-1-0.9-2-1.5-2.9c-0.4-0.7-0.9-1.3-1.3-1.6 c-0.4-0.4-0.8-0.5-1.2-0.5c-0.3,0-0.6,0.2-1,0.6c-0.4,0.4-0.5,0.7-0.5,1.1c0,0.3,0.2,0.9,0.5,1.6c0.4,0.7,0.8,1.4,1.4,2.2 c0.6,0.8,1.2,1.5,1.8,2C8.6,22.5,9,22.7,9.4,22.7z M11.5,12.3L9.1,5.6C8.5,3.9,8.1,2.7,7.7,2.2C7.4,1.7,7.1,1.4,6.7,1.4 c-0.3,0-0.5,0.1-0.7,0.3C5.7,2,5.6,2.3,5.6,2.7c0,0.7,0.3,1.8,0.8,3.4c0.5,1.6,1.3,3.8,2.3,6.4c0.1-0.2,0.2-0.3,0.4-0.3 c0.2-0.1,0.4-0.1,0.6-0.1c0.1,0,0.3,0,0.5,0C10.5,12.2,10.9,12.2,11.5,12.3z M13.9,18.8c-0.6,0-1.2-0.1-1.8-0.2 c-0.6-0.1-1.1-0.2-1.6-0.4c0.2,0.5,0.4,0.9,0.6,1.4c0.2,0.5,0.3,0.9,0.4,1.4c0.3-0.4,0.7-0.8,1.1-1.2 C13.1,19.4,13.5,19.1,13.9,18.8z M16.8,13c1-2.6,1.7-4.8,2.3-6.5c0.5-1.7,0.8-2.7,0.8-3.1c0-0.4-0.1-0.7-0.3-1 c-0.2-0.2-0.4-0.3-0.7-0.3c-0.4,0-0.8,0.3-1.2,1c-0.4,0.7-0.9,1.7-1.4,3.2l-2.2,6.2L16.8,13z" />
    </g>
  </svg>
);
const reddit: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M24 11.779c0-1.459-1.192-2.645-2.657-2.645-.715 0-1.363.286-1.84.746-1.81-1.191-4.259-1.949-6.971-2.046l1.483-4.669 4.016.941-.006.058c0 1.193.975 2.163 2.174 2.163 1.198 0 2.172-.97 2.172-2.163s-.975-2.164-2.172-2.164c-.92 0-1.704.574-2.021 1.379l-4.329-1.015c-.189-.046-.381.063-.44.249l-1.654 5.207c-2.838.034-5.409.798-7.3 2.025-.474-.438-1.103-.712-1.799-.712-1.465 0-2.656 1.187-2.656 2.646 0 .97.533 1.811 1.317 2.271-.052.282-.086.567-.086.857 0 3.911 4.808 7.093 10.719 7.093s10.72-3.182 10.72-7.093c0-.274-.029-.544-.075-.81.832-.447 1.405-1.312 1.405-2.318zm-17.224 1.816c0-.868.71-1.575 1.582-1.575.872 0 1.581.707 1.581 1.575s-.709 1.574-1.581 1.574-1.582-.706-1.582-1.574zm9.061 4.669c-.797.793-2.048 1.179-3.824 1.179l-.013-.003-.013.003c-1.777 0-3.028-.386-3.824-1.179-.145-.144-.145-.379 0-.523.145-.145.381-.145.526 0 .65.647 1.729.961 3.298.961l.013.003.013-.003c1.569 0 2.648-.315 3.298-.962.145-.145.381-.144.526 0 .145.145.145.379 0 .524zm-.189-3.095c-.872 0-1.581-.706-1.581-1.574 0-.868.709-1.575 1.581-1.575s1.581.707 1.581 1.575-.709 1.574-1.581 1.574z" />
  </svg>
);

const pinterest: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 774 1000.2">
    <path d="M0 359c0-42 8.8-83.7 26.5-125s43-79.7 76-115 76.3-64 130-86S345.7 0 411 0c106 0 193 32.7 261 98s102 142.3 102 231c0 114-28.8 208.2-86.5 282.5S555.3 723 464 723c-30 0-58.2-7-84.5-21s-44.8-31-55.5-51l-40 158c-3.3 12.7-7.7 25.5-13 38.5S259.8 873 253.5 885c-6.3 12-12.7 23.3-19 34s-12.7 20.7-19 30-11.8 17.2-16.5 23.5-9 11.8-13 16.5l-6 8c-2 2.7-4.7 3.7-8 3s-5.3-2.7-6-6c0-.7-.5-5.3-1.5-14s-2-17.8-3-27.5-2-22.2-3-37.5-1.3-30.2-1-44.5 1.3-30.2 3-47.5 4.2-33.3 7.5-48c7.3-31.3 32-135.7 74-313-5.3-10.7-9.7-23.5-13-38.5s-5-27.2-5-36.5l-1-15c0-42.7 10.8-78.2 32.5-106.5S303.3 223 334 223c24.7 0 43.8 8.2 57.5 24.5S412 284.3 412 309c0 15.3-2.8 34.2-8.5 56.5s-13.2 48-22.5 77-16 52.5-20 70.5c-6.7 30-.8 56 17.5 78s42.8 33 73.5 33c52.7 0 96.2-29.8 130.5-89.5S634 402.7 634 318c0-64.7-21-117.5-63-158.5S470.3 98 395 98c-84 0-152.2 27-204.5 81S112 297.7 112 373c0 44.7 12.7 82.3 38 113 8.7 10 11.3 20.7 8 32-1.3 3.3-3.3 11-6 23s-4.7 19.7-6 23c-1.3 7.3-4.7 12.2-10 14.5s-11.3 2.2-18-.5c-39.3-16-68.8-43.5-88.5-82.5S0 411 0 359z" />
  </svg>
);
const facebook: React.ReactNode = (
  <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">
    <title>facebook</title>
    <path
      className="cls-1"
      d="M22.23,21.56V15.78c0-2.77,1.83-3.43,3.16-3.43h8.09V0H22.35C10,0,7.19,9.14,7.19,15.08v6.48H0V36H7.27V72H21.68V36H32.35l.47-5.66.86-8.76Z"
    />
  </svg>
);

const facebookCircle: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
    <path d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"></path>
  </svg>
);
const google: React.ReactNode = (
  <svg id="googleIcon" viewBox="0 0 533.5 544.3" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M533.5 278.4c0-18.5-1.5-37.1-4.7-55.3H272.1v104.8h147c-6.1 33.8-25.7 63.7-54.4 82.7v68h87.7c51.5-47.4 81.1-117.4 81.1-200.2z"
      fill="#fff"
    />
    <path
      d="M272.1 544.3c73.4 0 135.3-24.1 180.4-65.7l-87.7-68c-24.4 16.6-55.9 26-92.6 26-71 0-131.2-47.9-152.8-112.3H28.9v70.1c46.2 91.9 140.3 149.9 243.2 149.9z"
      fill="#fff"
    />
    <path d="M119.3 324.3c-11.4-33.8-11.4-70.4 0-104.2V150H28.9c-38.6 76.9-38.6 167.5 0 244.4l90.4-70.1z" fill="#fff" />
    <path
      d="M272.1 107.7c38.8-.6 76.3 14 104.4 40.8l77.7-77.7C405 24.6 339.7-.8 272.1 0 169.2 0 75.1 58 28.9 150l90.4 70.1c21.5-64.5 81.8-112.4 152.8-112.4z"
      fill="#fff"
    />
  </svg>
);

const flag: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
    <path d="M0 0h24v24H0z" fill="none" />
    <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z" />
  </svg>
);

const hamburger: React.ReactNode = (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
    <path
      fill="currentColor"
      d="M16 132h416c8.837 0 16-7.163 16-16V76c0-8.837-7.163-16-16-16H16C7.163 60 0 67.163 0 76v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16z"
    />
  </svg>
);

const home: React.ReactNode = (
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 20">
    <path
      d="M14.9,6.4L8.6,0.1C8.5,0,8.3,0,8.2,0H7.8C7.7,0,7.5,0,7.4,0.1L1.1,6.4C1,6.5,1,6.7,1,6.8v8.7
	C1,15.8,1.2,16,1.5,16h4C5.8,16,6,15.8,6,15.5V11h4v4.5c0,0.3,0.2,0.5,0.5,0.5h4c0.3,0,0.5-0.2,0.5-0.5V6.8C15,6.7,15,6.5,14.9,6.4z
	"
    />
  </svg>
);

const images: React.ReactNode = (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 512 512"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M457.6 140.2l-82.5-4-4.8-53.8c-1-11.3-11.1-19.2-22.9-18.3L51.5 88.4c-11.8 1-20.3 10.5-19.4 21.7l21.2 235.8c1 11.3 11.2 19.2 22.9 18.3l15-1.2-2.4 45.8c-.6 12.6 9.2 22.8 22.4 23.5L441.3 448c13.2.6 24.1-8.6 24.8-21.2L480 163.5c.6-12.5-9.3-22.7-22.4-23.3zm-354.9 5.3l-7.1 134.8L78.1 305 62 127v-.5-.5c1-5 4.4-9 9.6-9.4l261-21.4c5.2-.4 9.7 3 10.5 7.9 0 .2.3.2.3.4 0 .1.3.2.3.4l2.7 30.8-219-10.5c-13.2-.4-24.1 8.8-24.7 21.3zm334 236.9l-84.8-99.5-37.4 34.3-69.2-80.8-122.7 130.7L133 168v-.4c1-5.4 6.2-9.3 11.9-9l291.2 14c5.8.3 10.3 4.7 10.4 10.2 0 .2.3.3.3.5l-10.1 199.1z" />
    <path d="M384 256c17.6 0 32-14.4 32-32s-14.3-32-32-32c-17.6 0-32 14.3-32 32s14.3 32 32 32z" />
  </svg>
);

const image: React.ReactNode = (
  <svg
    id="Layer_1"
    data-name="Layer 1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 135.2 135.2"
    className="addImageButton"
  >
    <title>-</title>
    <path d="M113.6,135.2h-92A21.6,21.6,0,0,1,0,113.6v-92A21.6,21.6,0,0,1,21.6,0h92a21.6,21.6,0,0,1,21.6,21.6v92A21.6,21.6,0,0,1,113.6,135.2ZM21.6,6A15.6,15.6,0,0,0,6,21.6v92a15.6,15.6,0,0,0,15.6,15.6h92a15.6,15.6,0,0,0,15.6-15.6v-92A15.6,15.6,0,0,0,113.6,6Z" />
    <polygon points="130.1 102.1 103.1 75.2 78.7 99.6 37.7 58.6 6.4 89.9 2.2 85.6 37.7 50.1 78.7 91.1 103.1 66.7 134.3 97.9 130.1 102.1" />
    <path d="M95,55a16.5,16.5,0,1,1,16.4-16.5A16.5,16.5,0,0,1,95,55Zm0-26.9a10.5,10.5,0,1,0,10.4,10.4A10.5,10.5,0,0,0,95,28.1Z" />
  </svg>
);

const info: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 330 330">
    <path
      d="M165,0C74.019,0,0,74.02,0,165.001C0,255.982,74.019,330,165,330s165-74.018,165-164.999C330,74.02,255.981,0,165,0z
  M165,300c-74.44,0-135-60.56-135-134.999C30,90.562,90.56,30,165,30s135,60.562,135,135.001C300,239.44,239.439,300,165,300z"
    />
    <path
      d="M164.998,70c-11.026,0-19.996,8.976-19.996,20.009c0,11.023,8.97,19.991,19.996,19.991
 c11.026,0,19.996-8.968,19.996-19.991C184.994,78.976,176.024,70,164.998,70z"
    />
    <path
      d="M165,140c-8.284,0-15,6.716-15,15v90c0,8.284,6.716,15,15,15c8.284,0,15-6.716,15-15v-90C180,146.716,173.284,140,165,140z
 "
    />
  </svg>
);

const instagram: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">
    <title>instagram</title>
    <path
      className="cls-1"
      d="M60.05,16.78a4.32,4.32,0,1,1-4.32-4.32A4.32,4.32,0,0,1,60.05,16.78ZM36.51,48a12,12,0,1,1,12-12A12,12,0,0,1,36.51,48Zm0-30.49A18.49,18.49,0,1,0,55,36,18.49,18.49,0,0,0,36.51,17.51Zm0-11c9.61,0,10.75,0,14.55.21a19.71,19.71,0,0,1,6.68,1.24,11.11,11.11,0,0,1,4.14,2.69,11.11,11.11,0,0,1,2.69,4.14,19.71,19.71,0,0,1,1.24,6.68C66,25.25,66,26.39,66,36s0,10.75-.21,14.55a19.71,19.71,0,0,1-1.24,6.68,11.11,11.11,0,0,1-2.69,4.14,11.26,11.26,0,0,1-4.14,2.7,20.23,20.23,0,0,1-6.68,1.24c-3.8.17-4.94.2-14.55.2s-10.75,0-14.55-.2a20.23,20.23,0,0,1-6.68-1.24,11.26,11.26,0,0,1-4.14-2.7,11.11,11.11,0,0,1-2.69-4.14,19.71,19.71,0,0,1-1.24-6.68C7,46.75,7,45.61,7,36s0-10.75.21-14.55a19.71,19.71,0,0,1,1.24-6.68,11.11,11.11,0,0,1,2.69-4.14,11.11,11.11,0,0,1,4.14-2.69A19.71,19.71,0,0,1,22,6.7C25.76,6.52,26.9,6.49,36.51,6.49Zm0-6.49c-9.78,0-11,0-14.84.22a26.33,26.33,0,0,0-8.74,1.67A17.64,17.64,0,0,0,6.55,6,17.64,17.64,0,0,0,2.4,12.42,26.33,26.33,0,0,0,.73,21.16C.55,25,.51,26.22.51,36s0,11,.22,14.84A26.33,26.33,0,0,0,2.4,59.58,17.64,17.64,0,0,0,6.55,66a17.64,17.64,0,0,0,6.38,4.15,26.33,26.33,0,0,0,8.74,1.67c3.84.18,5.06.22,14.84.22s11,0,14.84-.22a26.27,26.27,0,0,0,8.74-1.67A18.37,18.37,0,0,0,70.62,59.58a26.33,26.33,0,0,0,1.67-8.74c.18-3.84.22-5.06.22-14.84s0-11-.22-14.84a26.33,26.33,0,0,0-1.67-8.74A17.64,17.64,0,0,0,66.47,6a17.64,17.64,0,0,0-6.38-4.15A26.27,26.27,0,0,0,51.35.22C47.51,0,46.29,0,36.51,0Z"
    />
  </svg>
);

const library: React.ReactNode = (
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 125">
    <path d="M23.2,99.6h-11c-1.1,0-2-0.9-2-2V2.1c0-1.1,0.9-2,2-2h11c1.1,0,2,0.9,2,2v95.5C25.2,98.7,24.3,99.6,23.2,99.6z" />
    <path
      d="M40.1,99.6H29.2c-1.1,0-2-0.9-2-2V16.2c0-1.1,0.9-2,2-2h10.9c1.1,0,2,0.9,2,2v81.5
	C42.1,98.7,41.2,99.6,40.1,99.6z"
    />
    <path d="M56.8,99.6H46c-1.1,0-2-0.9-2-2V2c0-1.1,0.9-2,2-2h10.9c1.1,0,2,0.9,2,2v95.7C58.8,98.7,57.9,99.6,56.8,99.6z" />
    <path
      d="M93.1,96.6l-10.4,3.1c-1.1,0.3-2.2-0.3-2.5-1.3L60,30.8c-0.3-1.1,0.3-2.2,1.3-2.5l10.4-3.1
	c1.1-0.3,2.2,0.3,2.5,1.3l20.2,67.6C94.8,95.1,94.2,96.3,93.1,96.6z"
    />
  </svg>
);

const linkedinModal: React.ReactNode = (
  <svg id="linkedinIconModal" height="512pt" viewBox="0 0 512 512" width="512pt" xmlns="http://www.w3.org/2000/svg">
    <path d="m475.074219 0h-438.148438c-20.394531 0-36.925781 16.53125-36.925781 36.925781v438.148438c0 20.394531 16.53125 36.925781 36.925781 36.925781h438.148438c20.394531 0 36.925781-16.53125 36.925781-36.925781v-438.148438c0-20.394531-16.53125-36.925781-36.925781-36.925781zm-293.464844 387h-62.347656v-187.574219h62.347656zm-31.171875-213.1875h-.40625c-20.921875 0-34.453125-14.402344-34.453125-32.402344 0-18.40625 13.945313-32.410156 35.273437-32.410156 21.328126 0 34.453126 14.003906 34.859376 32.410156 0 18-13.53125 32.402344-35.273438 32.402344zm255.984375 213.1875h-62.339844v-100.347656c0-25.21875-9.027343-42.417969-31.585937-42.417969-17.222656 0-27.480469 11.601563-31.988282 22.800781-1.648437 4.007813-2.050781 9.609375-2.050781 15.214844v104.75h-62.34375s.816407-169.976562 0-187.574219h62.34375v26.558594c8.285157-12.78125 23.109375-30.960937 56.1875-30.960937 41.019531 0 71.777344 26.808593 71.777344 84.421874zm0 0" />
  </svg>
);
const link: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
    <path d="M326.612 185.391c59.747 59.809 58.927 155.698.36 214.59-.11.12-.24.25-.36.37l-67.2 67.2c-59.27 59.27-155.699 59.262-214.96 0-59.27-59.26-59.27-155.7 0-214.96l37.106-37.106c9.84-9.84 26.786-3.3 27.294 10.606.648 17.722 3.826 35.527 9.69 52.721 1.986 5.822.567 12.262-3.783 16.612l-13.087 13.087c-28.026 28.026-28.905 73.66-1.155 101.96 28.024 28.579 74.086 28.749 102.325.51l67.2-67.19c28.191-28.191 28.073-73.757 0-101.83-3.701-3.694-7.429-6.564-10.341-8.569a16.037 16.037 0 0 1-6.947-12.606c-.396-10.567 3.348-21.456 11.698-29.806l21.054-21.055c5.521-5.521 14.182-6.199 20.584-1.731a152.482 152.482 0 0 1 20.522 17.197zM467.547 44.449c-59.261-59.262-155.69-59.27-214.96 0l-67.2 67.2c-.12.12-.25.25-.36.37-58.566 58.892-59.387 154.781.36 214.59a152.454 152.454 0 0 0 20.521 17.196c6.402 4.468 15.064 3.789 20.584-1.731l21.054-21.055c8.35-8.35 12.094-19.239 11.698-29.806a16.037 16.037 0 0 0-6.947-12.606c-2.912-2.005-6.64-4.875-10.341-8.569-28.073-28.073-28.191-73.639 0-101.83l67.2-67.19c28.239-28.239 74.3-28.069 102.325.51 27.75 28.3 26.872 73.934-1.155 101.96l-13.087 13.087c-4.35 4.35-5.769 10.79-3.783 16.612 5.864 17.194 9.042 34.999 9.69 52.721.509 13.906 17.454 20.446 27.294 10.606l37.106-37.106c59.271-59.259 59.271-155.699.001-214.959z"></path>
  </svg>
);
const linkedin: React.ReactNode = (
  <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">
    <title>linkedin</title>
    <path
      id="LinkedIn"
      d="M72,44.07V70.68H56.57V45.85c0-6.24-2.23-10.5-7.82-10.5A8.45,8.45,0,0,0,40.83,41a10.47,10.47,0,0,0-.51,3.76V70.68H24.88s.21-42.06,0-46.42H40.32v6.58l-.1.15h.1v-.15c2-3.16,5.71-7.67,13.91-7.67C64.39,23.17,72,29.81,72,44.07ZM8.74,1.88C3.45,1.88,0,5.35,0,9.9s3.35,8,8.53,8h.1c5.39,0,8.73-3.57,8.73-8S14,1.88,8.74,1.88ZM.92,70.68H16.35V24.26H.92Z"
    />
  </svg>
);

const linkedinSquare: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
    <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path>
  </svg>
);
const maximize: React.ReactNode = (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
    <path d="M464 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h416c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zm0 394c0 3.3-2.7 6-6 6H54c-3.3 0-6-2.7-6-6V192h416v234z" />
  </svg>
);

const media: React.ReactNode = (
  <svg id="myMedia-icon" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 195 150">
    <title>media</title>
    <g>
      <path
        className="my-media-l1"
        d="M93.5,72.4c-2.2,0-4,1.9-4.1,4.1l-0.2,13c0,2.2-1.9,4-4.1,4H73.6c-2.2,0-5.1,1.4-6.5,3.1l-8.1,9.8
                        c-1.4,1.7-2.5,1.3-2.5-0.9l0-8c0-2.2-1.8-4-4-4H37.5c-2.2,0-4-1.8-4-4v-49c0-2.2,1.8-4,4-4h74c2.2,0,4,1.8,4,4l0,27.7
                        c0,2.2-1.8,4-4,4.1L93.5,72.4z"
      />
    </g>
    <g>
      <path
        className="my-media-l1"
        d="M171.5,125.7c0,2.1-1.7,3.8-3.8,3.8H93.3c-2.1,0-3.8-1.7-3.8-3.8V76.3c0-2.1,1.7-3.8,3.8-3.8h74.4
                        c2.1,0,3.8,1.7,3.8,3.8V125.7z"
      />
    </g>
    <line className="my-media-l2" x1="41.5" y1="50.5" x2="106.5" y2="50.5" />
    <line className="my-media-l2" x1="41.5" y1="63.5" x2="106.5" y2="63.5" />
    <line className="my-media-l2" x1="41.5" y1="76.5" x2="80.5" y2="76.5" />
    <polygon className="my-media-l3" points="122.5,111.6 123.2,88 143.3,100.4 " />
  </svg>
);

const minimize: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
    <path d="M0 0h24v24H0V0z" fill="none" />
    <path d="M6 19h12v2H6z" />
  </svg>
);

const newsletter: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">
    <title>newsletter</title>
    <path
      id="newsletter"
      d="M64,11.67H8.77C3.93,11.67,0,14.88,0,20V52a8.32,8.32,0,0,0,8.33,8.33H63.56A8.32,8.32,0,0,0,71.88,52V20A8.53,8.53,0,0,0,64,11.67ZM6.57,20.88,34.19,42.36a3.82,3.82,0,0,0,3.94,0h0L65.75,20.88V51.56a2.35,2.35,0,0,1-2.19,2.19H8.77a2.35,2.35,0,0,1-2.2-2.19ZM36.38,35.34l-23.23-18H59.61Z"
    />
  </svg>
);

const nominateStars: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 142.8 38.96">
    <title>Nominate-stars</title>
    <path
      className="cls-1"
      d="M11.4,17.2,15.16,24l7.64,1.46-5.32,5.66,1,7.69-7-3.29-7,3.29,1-7.69L0,25.45,7.64,24,11.4,17.2"
    />
    <path
      className="cls-1"
      d="M131.4,17.2,135.16,24l7.64,1.46-5.32,5.66,1,7.69-7-3.29-7.05,3.29,1-7.69L120,25.45,127.64,24l3.76-6.79"
    />
    <path
      className="cls-2"
      d="M71.4,0l6.26,11.31L90.4,13.75l-8.87,9.43L83.14,36,71.4,30.51,59.66,36l1.61-12.82L52.4,13.75l12.74-2.44L71.4,0"
    />
  </svg>
);

const onlineCourse: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">
    <title>e-learning</title>
    <path d="M66.85,43.88V32l1.91-1.13a1,1,0,0,0,0-1.67L35.11,9.29a1,1,0,0,0-1,0L.48,29.17a1,1,0,0,0,0,1.67l11.28,6.67v10.6a1,1,0,0,0,.49.85L34.14,61.27a.91.91,0,0,0,.48.13.88.88,0,0,0,.47-.13L57,49a1,1,0,0,0,.49-.85V37.51L61.78,35v9.28a7.21,7.21,0,1,0,5.07-.36Zm-50-3.38L34.12,50.72a1,1,0,0,0,1,0L52.4,40.5v5.22l-17.78,10-17.79-10ZM34.62,14.88,60.2,30,34.62,45.12,9,30Zm32.31,35.9a2.16,2.16,0,1,1-2.16-2.15A2.16,2.16,0,0,1,66.93,50.78Z" />
  </svg>
);

const overview: React.ReactNode = (
  <svg id="overview-icon" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">
    <title>overview-off</title>
    <path
      className="cls-1"
      d="M71.83,18.84,65.81,2A3,3,0,0,0,62,.17l-5.65,2A3,3,0,0,0,54.5,6l6,16.86a3,3,0,0,0,3.82,1.81l5.66-2A3,3,0,0,0,71.83,18.84Z"
    />
    <path className="cls-1" d="M32.43,13.75a3,3,0,0,0-1.83,3.81l4,11.23a3,3,0,0,0,3.83,1.82l19.25-6.79L51.68,7Z" />
    <path
      className="cls-1"
      d="M27.77,18.55,8,25.52a3,3,0,0,0-1.82,3.81l.5,1.41L1,32.74a1.49,1.49,0,1,0,1,2.8l5.66-2,.5,1.4A3,3,0,0,0,12,36.76l19.8-7Z"
    />
    <path
      className="cls-2"
      d="M39.51,36.73H42a1.31,1.31,0,0,0,.39,0,1.5,1.5,0,0,0,1.06-1.83A1.52,1.52,0,0,0,42,33.75H33a1.74,1.74,0,0,0-.39,0A1.49,1.49,0,0,0,33,36.73h2.49L21.2,61.32a1.49,1.49,0,0,0,.24,1.8,1.51,1.51,0,0,0,2.12,0,1.91,1.91,0,0,0,.24-.32l12.2-21V62.07a1.74,1.74,0,0,0,0,.39,1.5,1.5,0,0,0,3-.39V41.82l12.2,21a1.91,1.91,0,0,0,.24.32,1.52,1.52,0,0,0,2.13,0,1.5,1.5,0,0,0,.23-1.8Z"
    />
  </svg>
);

const pause: React.ReactNode = (
  <svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 535.578 535.578">
    <path
      d="M231.6,516.278c0,10.658-8.641,19.3-19.3,19.3H106.15c-10.659,0-19.3-8.641-19.3-19.3V19.3
  c0-10.659,8.641-19.3,19.3-19.3h106.15c10.659,0,19.3,8.641,19.3,19.3V516.278z"
    />
    <path
      d="M448.728,516.278c0,10.658-8.641,19.3-19.3,19.3h-106.15c-10.659,0-19.3-8.641-19.3-19.3V19.3
  c0-10.659,8.641-19.3,19.3-19.3h106.15c10.659,0,19.3,8.641,19.3,19.3V516.278z"
    />
  </svg>
);

const pauseCircle: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="play-video">
    <path d="M218 160h-20c-3.3 0-6 2.7-6 6v180c0 3.3 2.7 6 6 6h20c3.3 0 6-2.7 6-6V166c0-3.3-2.7-6-6-6zm96 0h-20c-3.3 0-6 2.7-6 6v180c0 3.3 2.7 6 6 6h20c3.3 0 6-2.7 6-6V166c0-3.3-2.7-6-6-6zM256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm0 464c-118.7 0-216-96.1-216-216 0-118.7 96.1-216 216-216 118.7 0 216 96.1 216 216 0 118.7-96.1 216-216 216z" />
  </svg>
);

const pencil: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72" className="edit-svg">
    <title>edit</title>
    <path d="M8.9,70.89a3,3,0,0,0,1.76.58,2.94,2.94,0,0,0,1.26-.29L25.09,65A3.11,3.11,0,0,0,26.21,64L56,23.51h0l7.6-10.33a4.93,4.93,0,0,0-1-6.89l-8-5.85a4.87,4.87,0,0,0-2.92-1,4.94,4.94,0,0,0-4,2L10.31,52.33a2.82,2.82,0,0,0-.54,1.34l-2,14.42A2.93,2.93,0,0,0,8.9,70.89Zm43-65.11,6.35,4.68L53,17.61l-6.36-4.68ZM15.52,55.24,43.15,17.7l6.36,4.68L21.88,59.92l-7.52,3.56Z" />
  </svg>
);

const people: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" className="newIcone">
    <circle cx="49.99" cy="29.39" r="15.9" />
    <path d="M71.83,70.94v9.88a5.71,5.71,0,0,1-5.7,5.71H33.86a5.71,5.71,0,0,1-5.7-5.71V70.94a21.33,21.33,0,0,1,3.19-11.42h0a21.81,21.81,0,0,1,36.91,0h0a.49.49,0,0,0,.22.27h0A21.71,21.71,0,0,1,71.83,70.94Z" />
    <path d="M21.91,80.82a9.5,9.5,0,0,0,1.91,5.71H5.71A5.71,5.71,0,0,1,0,80.82V76.49A18.41,18.41,0,0,1,15.83,58.12a17.53,17.53,0,0,1,2,0,17.3,17.3,0,0,1,6.81,1.37A25.16,25.16,0,0,0,21.91,70.9Z" />
    <path d="M100,76.49v4.33a5.71,5.71,0,0,1-5.71,5.71H76.17a9.49,9.49,0,0,0,1.9-5.71V70.94A25.06,25.06,0,0,0,75.3,59.52a17.66,17.66,0,0,1,6.81-1.37A17.85,17.85,0,0,1,100,76c0,.23,0,.46,0,.69Z" />
    <circle cx="15.52" cy="41.19" r="13.01" />
    <circle cx="84.47" cy="41.19" r="13.01" />
  </svg>
);

const peopleAlt: React.ReactNode = (
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 504.4 368.8">
    <g>
      <path d="M252.2,4.4c38.3,0,69.4,31.1,69.4,69.4s-31,69.4-69.4,69.4c-38.3,0-69.4-31.1-69.4-69.4S213.9,4.4,252.2,4.4z" />
      <path
        d="M252.2,155.4c53.2,0,98.8,38.1,117.7,92.1c-30.5,29.4-72,47.5-117.7,47.5s-87.2-18.1-117.7-47.6
		C153.4,193.5,199,155.4,252.2,155.4L252.2,155.4z"
      />
      <path d="M407.9,0c31.4,0,56.9,25.5,56.9,56.9s-25.5,56.9-56.9,56.9c-31.4,0-56.9-25.5-56.9-56.9S376.4,0,407.9,0z" />
      <path
        d="M407.9,123.8c43.6,0,81,31.2,96.5,75.5c-25,24.1-59,39-96.5,39c-6.5,0-13-0.4-19.3-1.3
		c-11.4-30.3-30.9-57.1-56-75.7C351.6,138.2,378.3,123.8,407.9,123.8L407.9,123.8z"
      />
      <path d="M96.5,0c31.4,0,56.9,25.5,56.9,56.9s-25.5,56.9-56.9,56.9c-31.4,0-56.9-25.5-56.9-56.9S65.1,0,96.5,0z" />
      <path
        d="M96.5,123.8c29.6,0,56.3,14.4,75.3,37.4c-25.1,18.6-44.6,45.4-56,75.7c-6.3,0.9-12.7,1.3-19.3,1.3
		c-37.5,0-71.5-14.9-96.5-39C15.5,155,52.9,123.8,96.5,123.8L96.5,123.8z"
      />
    </g>
  </svg>
);

const peopleThin: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 458.53 257.27" className="we-are">
    <title>We</title>
    <g id="Layer_1" data-name="Layer 1">
      <path
        className="are cls-1"
        d="M402.4,134.08l-2-1.26,1.31-1.92a58.12,58.12,0,0,0,10-33c0-29.59-21.67-51.06-51.53-51.06-30,0-51.79,21.47-51.79,51.06a60.53,60.53,0,0,0,10,33.26l1.25,1.91-1.92,1.24a143.64,143.64,0,0,0-13.28,10.48l8.34,8.8a78.79,78.79,0,0,1,14.27-10.72l1.32-.77,1.21.94a49.61,49.61,0,0,0,30.57,11,47.41,47.41,0,0,0,30-10.67l1.2-1,1.34.76c25.49,14.49,43.29,45.13,43.29,74.51a61.07,61.07,0,0,1-.8,9.69h12.63a65.68,65.68,0,0,0,.64-9.69C448.55,185.47,430,151.88,402.4,134.08Zm-42.17,7.22c-21.54,0-39.06-19.46-39.06-43.37,0-22.57,16.06-38.33,39.06-38.33s39.06,15.76,39.06,38.33C399.29,121.84,381.77,141.3,360.23,141.3Z"
      />
      <path
        className="are cls-1"
        d="M131.35,142.13l1.32.77a79.08,79.08,0,0,1,14.26,10.72l8.35-8.8A145,145,0,0,0,142,134.34l-1.92-1.24,1.25-1.91a60.52,60.52,0,0,0,10-33.26c0-29.59-21.78-51.06-51.78-51.06S48,68.34,48,97.93a58.19,58.19,0,0,0,10,33l1.31,1.92-2,1.26c-27.6,17.8-46.15,51.39-46.15,83.58a67.17,67.17,0,0,0,.63,9.69H24.51a61,61,0,0,1-.79-9.69c0-29.38,17.8-60,43.29-74.51l1.34-.76,1.19,1a47.44,47.44,0,0,0,30,10.67,49.61,49.61,0,0,0,30.57-11Zm-31.78-.83c-21.54,0-39.06-19.46-39.06-43.37,0-22.57,16.06-38.33,39.06-38.33s39.06,15.76,39.06,38.33C138.63,121.84,121.1,141.3,99.57,141.3Z"
      />
      <path
        className="are cls-1"
        d="M278.77,128.28l-4-2.56,2.66-3.9A66.76,66.76,0,0,0,288.93,84c0-33.79-24.76-58.32-58.88-58.32-34.29,0-59.18,24.53-59.18,58.32a69.52,69.52,0,0,0,11.51,38.2l2.55,3.89L181,128.59C149,149.16,127.44,188,127.44,225.31a84.8,84.8,0,0,0,.65,9.5h11.16a70.9,70.9,0,0,1-.65-9.5c0-35.24,21.14-72.21,51.41-89.91l2.69-1.57,2.46,1.91c10.52,8.19,22.59,12.52,34.89,12.52a54,54,0,0,0,34.22-12.16l2.44-2,2.72,1.55c30.66,17.43,52.08,54.29,52.08,89.63a73.72,73.72,0,0,1-.64,9.5h11a81.44,81.44,0,0,0,.51-9.5C332.35,187.94,310.82,149,278.77,128.28Zm-48.72,8.83c-26.48,0-48-23.83-48-53.12,0-27.77,19.75-47.17,48-47.17s48,19.4,48,47.17C278.08,113.28,256.54,137.11,230.05,137.11Z"
      />
    </g>
  </svg>
);

const personAlt: React.ReactNode = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    version="1.1"
    id="Layer_1"
    x="0px"
    y="0px"
    viewBox="0 0 258.75 258.75"
    className="noImageIcon"
  >
    <g>
      <circle cx="129.375" cy="60" r="60" fill="#a2a7ab" />
      <path
        d="M129.375,150c-60.061,0-108.75,48.689-108.75,108.75h217.5C238.125,198.689,189.436,150,129.375,150z"
        fill="#a2a7ab"
      />
    </g>
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
  </svg>
);

const muted: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M19 7.358v15.642l-8-5v-.785l8-9.857zm3-6.094l-1.548-1.264-3.446 4.247-6.006 3.753v3.646l-2 2.464v-6.11h-4v10h.843l-3.843 4.736 1.548 1.264 18.452-22.736z" />
  </svg>
);

const unmuted: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M6 7l8-5v20l-8-5v-10zm-6 10h4v-10h-4v10zm20.264-13.264l-1.497 1.497c1.847 1.783 2.983 4.157 2.983 6.767 0 2.61-1.135 4.984-2.983 6.766l1.498 1.498c2.305-2.153 3.735-5.055 3.735-8.264s-1.43-6.11-3.736-8.264zm-.489 8.264c0-2.084-.915-3.967-2.384-5.391l-1.503 1.503c1.011 1.049 1.637 2.401 1.637 3.888 0 1.488-.623 2.841-1.634 3.891l1.503 1.503c1.468-1.424 2.381-3.309 2.381-5.394z" />
  </svg>
);

const play: React.ReactNode = (
  <svg
    version="1.1"
    id="Capa_1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    x="0px"
    y="0px"
    width="163.861px"
    height="163.861px"
    viewBox="0 0 163.861 163.861"
    xmlSpace="preserve"
  >
    <g>
      <path
        d="M34.857,3.613C20.084-4.861,8.107,2.081,8.107,19.106v125.637c0,17.042,11.977,23.975,26.75,15.509L144.67,97.275
		c14.778-8.477,14.778-22.211,0-30.686L34.857,3.613z"
      />
    </g>
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
  </svg>
);

const playCircle: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="play-video">
    <path d="M256 504c137 0 248-111 248-248S393 8 256 8 8 119 8 256s111 248 248 248zM40 256c0-118.7 96.1-216 216-216 118.7 0 216 96.1 216 216 0 118.7-96.1 216-216 216-118.7 0-216-96.1-216-216zm331.7-18l-176-107c-15.8-8.8-35.7 2.5-35.7 21v208c0 18.4 19.8 29.8 35.7 21l176-101c16.4-9.1 16.4-32.8 0-42zM192 335.8V176.9c0-4.7 5.1-7.6 9.1-5.1l134.5 81.7c3.9 2.4 3.8 8.1-.1 10.3L201 341c-4 2.3-9-.6-9-5.2z" />
  </svg>
);

const playCircleSolid: React.ReactNode = (
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 125">
    <desc>Play icon circle solid.</desc>
    <path
      className="play-icon-circle-solid"
      d="M0,50C0,22.4,22.4,0,50,0c27.6,0,50,22.4,50,50c0,27.6-22.4,50-50,50C22.4,100,0,77.6,0,50L0,50z M69.6,52.5
			L43.7,69.4c-2.1,1.4-3.7,0.5-3.7-2V32.6c0-2.4,1.7-3.3,3.7-2l25.8,16.9C71.6,48.9,71.6,51.1,69.6,52.5L69.6,52.5z"
    />
  </svg>
);

const playSquare: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" className="newIcone">
    <path d="M87.22,90.86H13.82A13.84,13.84,0,0,1,0,77V26.23A13.84,13.84,0,0,1,13.82,12.41h73.4c7.41,0,12.78,5.81,12.78,13.82V77C100,85,94.63,90.86,87.22,90.86ZM13.82,20.75a5.48,5.48,0,0,0-5.47,5.48V77a5.48,5.48,0,0,0,5.47,5.48h73.4c3.27,0,4.43-3,4.43-5.48V26.23c0-2.53-1.16-5.48-4.43-5.48Z" />
    <path d="M67.94,54.35l-28,16.07c-5.55,2.92-5.69-2.49-5.69-2.49V35.51c.1-5.7,5.4-2.78,5.4-2.78l28,16.65C70.86,51.72,67.94,54.35,67.94,54.35Z" />
  </svg>
);

const playWithSpaces: React.ReactNode = (
  <svg className="play-btn" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M8,6.822v10.357c0,0.789,0.871,1.267,1.537,0.844l8.137-5.178c0.618-0.393,0.618-1.294,0-1.687L9.537,5.978 C8.871,5.554,8,6.033,8,6.822z" />
  </svg>
);

const plus: React.ReactNode = (
  <svg
    version="1.1"
    id="Capa_1"
    xmlns="http://www.w3.org/2000/svg"
    x="0px"
    y="0px"
    viewBox="0 0 31.444 31.444"
    className="plus-icon"
  >
    <path
      d="M1.119,16.841c-0.619,0-1.111-0.508-1.111-1.127c0-0.619,0.492-1.111,1.111-1.111h13.475V1.127
        C14.595,0.508,15.103,0,15.722,0c0.619,0,1.111,0.508,1.111,1.127v13.476h13.475c0.619,0,1.127,0.492,1.127,1.111
        c0,0.619-0.508,1.127-1.127,1.127H16.833v13.476c0,0.619-0.492,1.127-1.111,1.127c-0.619,0-1.127-0.508-1.127-1.127V16.841H1.119z"
    />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
  </svg>
);

const plusAlt: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
    <path d="M16,0a3,3,0,0,0-3,3V13H3a3,3,0,0,0,0,6H13V29a3,3,0,0,0,6,0V19H29a3,3,0,0,0,0-6H19V3A3,3,0,0,0,16,0Z" />
  </svg>
);

const profilePreview: React.ReactNode = (
  <svg
    id="Layer_1"
    data-name="Layer 1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 72 72"
    className="view-and-configure-icon"
  >
    <title>profile-preview</title>
    <g id="PNQ5SD">
      <path
        className="cls-1"
        d="M35.93,58.79H17.73c-1.23,0-1.28-.07-1.17-1.32a11.22,11.22,0,0,1,9.1-10.27A12.29,12.29,0,0,1,27.93,47q8.1,0,16.21,0a11.28,11.28,0,0,1,11.34,10.9c0,.75-.12.9-.91.9Z"
      />
      <path className="cls-1" d="M36,44A11.57,11.57,0,1,1,47.56,32.4,11.53,11.53,0,0,1,36,44Z" />
    </g>
    <path className="cls-2" d="M6.86,57H65.14a3.33,3.33,0,0,0,3.33-3.32V9.91H3.53V53.66A3.33,3.33,0,0,0,6.86,57Z" />
    <path className="cls-2" d="M8.47,3.78a2,2,0,0,0-2,2,2,2,0,0,0,1.94,2,2.05,2.05,0,0,0,2.07-2A2,2,0,0,0,8.47,3.78Z" />
    <path className="cls-2" d="M16.75,3.78a2,2,0,0,0-2,2,2,2,0,0,0,2,2,2.07,2.07,0,0,0,2-2A2,2,0,0,0,16.75,3.78Z" />
    <path className="cls-2" d="M25.09,3.78a2,2,0,0,0-2,2,2,2,0,0,0,2,2.06,2,2,0,0,0,2-2A2,2,0,0,0,25.09,3.78Z" />
    <path className="cls-3" d="M0,55.82H.05a6.06,6.06,0,0,1,0-.76Z" />
    <path className="cls-3" d="M3.53,53.66C2.41,54,1.22,55,0,55.07a6.06,6.06,0,0,0,.05.76,15.39,15.39,0,0,1,3.48.64Z" />
    <path
      className="cls-3"
      d="M65.14,0H6.86A6.87,6.87,0,0,0,0,6.86V55.07C1.22,55,2.41,54,3.53,53.66V9.91H68.47V53.66A3.33,3.33,0,0,1,65.14,57H6.86a3.33,3.33,0,0,1-3.33-3.32v2.81a15.39,15.39,0,0,0-3.48-.64c.38,3.24,3.29,4.69,6.81,4.69H65.14A6.87,6.87,0,0,0,72,53.66V6.86A6.87,6.87,0,0,0,65.14,0ZM8.39,7.78a2,2,0,0,1-1.94-2,2,2,0,0,1,2-2,2,2,0,0,1,2,2A2.05,2.05,0,0,1,8.39,7.78Zm8.42,0a2,2,0,0,1-2-2,2,2,0,0,1,2-2,2,2,0,0,1,2,2A2.07,2.07,0,0,1,16.81,7.79Zm8.25,0a2,2,0,0,1-2-2.06,2,2,0,0,1,4,.06A2,2,0,0,1,25.06,7.79Z"
    />
  </svg>
);

const radivisionPlay: React.ReactNode = (
  <svg
    id="Layer_1"
    data-name="Layer 1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 101.57 101.57"
    className="playSvg"
  >
    <circle className="cls-1" cx="50.79" cy="50.79" r="34.2" />
    <path
      className="cls-2"
      d="M42,68.12a2,2,0,0,1-2-2V36.94a1.95,1.95,0,0,1,3-1.66l24.2,14.33a1.94,1.94,0,0,1,0,3.3L43.06,67.81A1.92,1.92,0,0,1,42,68.12Z"
    />
    <path
      className="cls-2"
      d="M42,35.49a1.41,1.41,0,0,1,.75.22L67,50a1.44,1.44,0,0,1,0,2.45L42.79,67.39a1.47,1.47,0,0,1-.77.23,1.44,1.44,0,0,1-1.45-1.45V36.94A1.44,1.44,0,0,1,42,35.49m0-1h0a2.45,2.45,0,0,0-2.45,2.45V66.17a2.44,2.44,0,0,0,3.76,2.06l24.2-14.88a2.45,2.45,0,0,0,1.15-2.1,2.43,2.43,0,0,0-1.17-2.06L43.28,34.85A2.4,2.4,0,0,0,42,34.49Z"
    />
  </svg>
);

const radivisionStar: React.ReactNode = (
  <svg
    id="Layer_1"
    className="star-svg northStarSvg"
    data-name="Layer 1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 72 72"
  >
    <title>star on </title>
    <polygon className="cls-1 star-1 st2" points="3.5 35.84 3.5 35.84 33.11 44.31 33.11 27.36 3.5 35.84" />
    <polygon className="cls-1 star-1 st2" points="69.5 35.84 69.5 35.84 39.89 27.36 39.89 44.31 69.5 35.84" />
    <polygon className="cls-1 star-1 st2" points="36.5 71.84 36.5 71.84 44.97 39.23 28.02 39.23 36.5 71.84" />
    <polygon className="cls-2 star-2 st0" points="36.5 -0.16 36.5 -0.16 28.02 32.45 44.97 32.45 36.5 -0.16" />
    <circle
      className="cls-3 star-0 st1"
      cx="36.5"
      cy="35.84"
      r="10.3"
      transform="translate(-6.75 8.52) rotate(-12.2)"
    />
  </svg>
);

const redHeart: React.ReactNode = (
  <svg viewBox="0 0 24 24" className="r-4qtqp9 r-yyyyoo r-1xvli5t r-dnmrzs r-bnwqim r-1plcrui r-lrvibr r-1hdv0qi">
    <g>
      <path d="M12 21.638h-.014C9.403 21.59 1.95 14.856 1.95 8.478c0-3.064 2.525-5.754 5.403-5.754 2.29 0 3.83 1.58 4.646 2.73.814-1.148 2.354-2.73 4.645-2.73 2.88 0 5.404 2.69 5.404 5.755 0 6.376-7.454 13.11-10.037 13.157H12zM7.354 4.225c-2.08 0-3.903 1.988-3.903 4.255 0 5.74 7.034 11.596 8.55 11.658 1.518-.062 8.55-5.917 8.55-11.658 0-2.267-1.823-4.255-3.903-4.255-2.528 0-3.94 2.936-3.952 2.965-.23.562-1.156.562-1.387 0-.014-.03-1.425-2.965-3.954-2.965z"></path>
    </g>
  </svg>
);

const reply: React.ReactNode = (
  <svg
    id="Capa_1"
    enableBackground="new 0 0 511.072 511.072"
    height="512"
    viewBox="0 0 511.072 511.072"
    width="512"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Speech_Bubble_48_">
      <g>
        <path d="m74.39 480.536h-36.213l25.607-25.607c13.807-13.807 22.429-31.765 24.747-51.246-36.029-23.644-62.375-54.751-76.478-90.425-14.093-35.647-15.864-74.888-5.121-113.482 12.89-46.309 43.123-88.518 85.128-118.853 45.646-32.963 102.47-50.387 164.33-50.387 77.927 0 143.611 22.389 189.948 64.745 41.744 38.159 64.734 89.63 64.734 144.933 0 26.868-5.471 53.011-16.26 77.703-11.165 25.551-27.514 48.302-48.593 67.619-46.399 42.523-112.042 65-189.83 65-28.877 0-59.01-3.855-85.913-10.929-25.465 26.123-59.972 40.929-96.086 40.929zm182-420c-124.039 0-200.15 73.973-220.557 147.285-19.284 69.28 9.143 134.743 76.043 175.115l7.475 4.511-.23 8.727c-.456 17.274-4.574 33.912-11.945 48.952 17.949-6.073 34.236-17.083 46.99-32.151l6.342-7.493 9.405 2.813c26.393 7.894 57.104 12.241 86.477 12.241 154.372 0 224.682-93.473 224.682-180.322 0-46.776-19.524-90.384-54.976-122.79-40.713-37.216-99.397-56.888-169.706-56.888z" />
      </g>
    </g>
  </svg>
);

const search: React.ReactNode = (
  <svg id="search" data-name="search" xmlns="http://www.w3.org/2000/svg" width="20px" viewBox="0 0 72 72">
    <title>search</title>
    <path d="M31.08,0c1.35.23,2.73.37,4.06.71,11.59,3,19,10.29,21.74,21.92,2.1,8.93-.07,17.1-5.75,24.33-.07.09-.15.17-.22.27s0,.08-.07.17c.14.16.28.34.45.5L71.51,68.12a5.6,5.6,0,0,0,.49.36v.29L68.77,72h-.29a2.69,2.69,0,0,0-.31-.44L47.8,51.19a4.74,4.74,0,0,0-.39-.36c-.14.09-.28.16-.4.25a27.49,27.49,0,0,1-14.28,6.24C22.56,58.53,14,55.33,7.08,47.7A28,28,0,0,1,.32,33.29C.2,32.56.11,31.81,0,31.08V26.44c.23-1.35.38-2.73.72-4.05Q5.19,5.2,22.38.72C23.71.37,25.09.23,26.44,0ZM52.75,28.77a24,24,0,1,0-24.24,24A24,24,0,0,0,52.75,28.77Z" />
  </svg>
);

const searchAlt: React.ReactNode = (
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1280">
    <path
      d="M647.5,256c10,10,19.3,20.7,28,31.8c-2.6-3.4-5.2-6.7-7.8-10.1c17,22.2,31.1,46.4,42.1,72.1
			c-1.7-4-3.4-8-5-11.9c11.3,26.9,18.9,55.1,22.8,84c-0.6-4.4-1.2-8.9-1.8-13.3c3.7,28.7,3.7,57.7,0,86.4c0.6-4.4,1.2-8.9,1.8-13.3
			c-3.9,28.9-11.5,57.1-22.8,84c1.7-4,3.4-8,5-11.9c-10.9,25.7-25,49.9-42.1,72.1c2.6-3.4,5.2-6.7,7.8-10.1
			c-17.3,22.4-37.5,42.5-59.8,59.8c3.4-2.6,6.7-5.2,10.1-7.8c-22.2,17-46.4,31.1-72.1,42.1c4-1.7,8-3.4,11.9-5
			c-26.9,11.3-55.1,18.9-84,22.8c4.4-0.6,8.9-1.2,13.3-1.8c-28.7,3.7-57.7,3.7-86.4,0c4.4,0.6,8.9,1.2,13.3,1.8
			c-28.9-3.9-57.1-11.5-84-22.8c4,1.7,8,3.4,11.9,5c-25.7-10.9-49.9-25-72.1-42.1c3.4,2.6,6.7,5.2,10.1,7.8
			c-22.4-17.3-42.5-37.5-59.8-59.8c2.6,3.4,5.2,6.7,7.8,10.1c-17-22.2-31.1-46.4-42.1-72.1c1.7,4,3.4,8,5,11.9
			c-11.3-26.9-18.9-55.1-22.8-84c0.6,4.4,1.2,8.9,1.8,13.3c-3.7-28.7-3.7-57.7,0-86.4c-0.6,4.4-1.2,8.9-1.8,13.3
			c3.9-28.9,11.5-57.1,22.8-84c-1.7,4-3.4,8-5,11.9c10.9-25.7,25-49.9,42.1-72.1c-2.6,3.4-5.2,6.7-7.8,10.1
			c17.3-22.4,37.5-42.5,59.8-59.8c-3.4,2.6-6.7,5.2-10.1,7.8c22.2-17,46.4-31.1,72.1-42.1c-4,1.7-8,3.4-11.9,5
			c26.9-11.3,55.1-18.9,84-22.8c-4.4,0.6-8.9,1.2-13.3,1.8c28.7-3.7,57.7-3.7,86.4,0c-4.4-0.6-8.9-1.2-13.3-1.8
			c28.9,3.9,57.1,11.5,84,22.8c-4-1.7-8-3.4-11.9-5c25.7,10.9,49.9,25,72.1,42.1c-3.4-2.6-6.7-5.2-10.1-7.8
			C626.8,236.7,637.5,246,647.5,256c9.1,9.1,22.5,14.6,35.4,14.6c12.3,0,26.9-5.4,35.4-14.6c8.7-9.5,15.2-22,14.6-35.4
			c-0.6-13.2-5.1-25.8-14.6-35.4c-53.7-53.3-124.3-91.5-199.2-104c-20.2-3.4-40.7-5.8-61.2-6.2c-18.8-0.4-37.4,1.2-56,3.7
			c-18.7,2.5-37.1,5.8-55.2,11.1c-19.7,5.8-38.9,13.5-57.6,22.1c-34.5,16-66.3,38.4-94.2,64.1c-28.5,26.3-53.2,57.8-72.1,91.7
			c-18,32.4-31.8,68-39.3,104.4s-10.6,74.1-7,111.2c3.6,37.5,11.4,74.1,25.9,108.9c8,19.2,16.6,38,27.6,55.7
			c11.1,18.1,24.1,35.2,37.9,51.4c25.2,29.4,55.9,54.9,88.9,75c31.7,19.3,67.2,34.1,103.3,43c72.1,17.8,150.8,14.8,220.5-11.5
			c19.5-7.4,38.8-15.8,57-26.1c18.3-10.3,35.1-22.7,51.6-35.7c30.6-24.2,56.7-54,78.2-86.4c41.3-62.2,62.8-138.8,61.1-213.4
			c-1.7-74.9-25.6-150.6-70.1-211.2c-12.4-16.8-25.5-32.9-40.2-47.7c-9-9.1-22.6-14.6-35.4-14.6c-12.3,0-26.9,5.4-35.4,14.6
			c-8.7,9.5-15.2,22-14.6,35.4C633.4,233.9,637.9,246.4,647.5,256z"
    />
    <path
      d="M647.5,718.2c24.3,24.3,48.7,48.7,73,73c38.8,38.8,77.6,77.6,116.4,116.4c8.9,8.9,17.9,17.9,26.8,26.8
			c9.1,9.1,22.5,14.6,35.4,14.6c12.3,0,26.9-5.4,35.4-14.6c8.7-9.5,15.2-22,14.6-35.4c-0.6-13.3-5.1-25.8-14.6-35.4
			c-24.3-24.3-48.7-48.7-73-73c-38.8-38.8-77.6-77.6-116.4-116.4c-8.9-8.9-17.9-17.9-26.8-26.8c-9.1-9.1-22.5-14.6-35.4-14.6
			c-12.3,0-26.9,5.4-35.4,14.6c-8.7,9.5-15.2,22-14.6,35.4C633.4,696.1,637.9,708.6,647.5,718.2L647.5,718.2z"
    />
  </svg>
);

const send: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
    <path d="M0 0h24v24H0z" fill="none" />
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const socialPost: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128.3 128.3">
    <title>Post an update</title>
    <path
      d="M83.3,11.1c-8.5,0-15.5,6.9-15.5,15.5c0,3.4,1.1,6.5,2.9,9L40.9,65.4c-2-1.3-4.3-2.1-6.9-2.1c-6.9,0-12.5,5.6-12.5,12.5
        c0,6.9,5.6,12.5,12.5,12.5c2.5,0,4.9-0.8,6.9-2.1l21.6,21.6c-0.5,1.1-0.9,2.3-0.9,3.6c0,4.4,3.6,8,8,8s8-3.6,8-8c0-4.4-3.6-8-8-8
        c-1.3,0-2.5,0.3-3.6,0.9L44.4,82.7c1.3-2,2.1-4.3,2.1-6.9c0-2.5-0.8-4.9-2.1-6.9l29.9-29.9c2.5,1.8,5.7,2.9,9,2.9
        c8.5,0,15.5-6.9,15.5-15.5S91.9,11.1,83.3,11.1z"
    />
  </svg>
);

const spinner: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
    <circle
      cx="50"
      cy="50"
      fill="none"
      stroke="#4cb4e7"
      strokeWidth="10"
      r="35"
      strokeDasharray="164.93361431346415 56.97787143782138"
    >
      <animateTransform
        attributeName="transform"
        type="rotate"
        repeatCount="indefinite"
        dur="1s"
        values="0 50 50;360 50 50"
        keyTimes="0;1"
      ></animateTransform>
    </circle>
  </svg>
);

const star: React.ReactNode = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
    <title>Star</title>
    <path d="M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z" />
  </svg>
);

const swap: React.ReactNode = (
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <title>swap_horiz</title>
    <path d="M21 9l-3.984 3.984v-3h-7.031v-1.969h7.031v-3zM6.984 11.016v3h7.031v1.969h-7.031v3l-3.984-3.984z" />
  </svg>
);

const trash: React.ReactNode = (
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <title>Delete Item</title>
    <path d="M4 10v20c0 1.1 0.9 2 2 2h18c1.1 0 2-0.9 2-2v-20h-22zM10 28h-2v-14h2v14zM14 28h-2v-14h2v14zM18 28h-2v-14h2v14zM22 28h-2v-14h2v14z" />
    <path d="M26.5 4h-6.5v-2.5c0-0.825-0.675-1.5-1.5-1.5h-7c-0.825 0-1.5 0.675-1.5 1.5v2.5h-6.5c-0.825 0-1.5 0.675-1.5 1.5v2.5h26v-2.5c0-0.825-0.675-1.5-1.5-1.5zM18 4h-6v-1.975h6v1.975z" />
  </svg>
);

const twitter: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512">
    <path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z" />
  </svg>
);

const lock: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M18 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-3zm-10-4c0-2.206 1.795-4 4-4s4 1.794 4 4v4h-8v-4zm11 16h-14v-10h14v10zm-7.737-2l-2.843-2.756 1.173-1.173 1.67 1.583 3.563-3.654 1.174 1.173-4.737 4.827z" />
  </svg>
);

const verified: React.ReactNode = (
  <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="verifiedIcon">
    <g id="_x3C_Group_x3E_">
      <path
        className="st0"
        d="M512,268c0,17.9-4.3,34.5-12.9,49.7c-8.6,15.2-20.1,27.1-34.6,35.4c0.4,2.7,0.6,6.9,0.6,12.6
      c0,27.1-9.1,50.1-27.1,69.1c-18.1,19.1-39.9,28.6-65.4,28.6c-11.4,0-22.3-2.1-32.6-6.3c-8,16.4-19.5,29.6-34.6,39.7
      C290.4,507,273.9,512,256,512c-18.3,0-34.9-4.9-49.7-14.9c-14.9-9.9-26.3-23.2-34.3-40c-10.3,4.2-21.1,6.3-32.6,6.3
      c-25.5,0-47.4-9.5-65.7-28.6c-18.3-19-27.4-42.1-27.4-69.1c0-3,0.4-7.2,1.1-12.6c-14.5-8.4-26-20.2-34.6-35.4
      C4.3,302.5,0,285.9,0,268c0-19,4.8-36.5,14.3-52.3c9.5-15.8,22.3-27.5,38.3-35.1c-4.2-11.4-6.3-22.9-6.3-34.3
      c0-27,9.1-50.1,27.4-69.1c18.3-19,40.2-28.6,65.7-28.6c11.4,0,22.3,2.1,32.6,6.3c8-16.4,19.5-29.6,34.6-39.7
      C221.6,5.1,238.1,0,256,0c17.9,0,34.4,5.1,49.4,15.1c15,10.1,26.6,23.3,34.6,39.7c10.3-4.2,21.1-6.3,32.6-6.3
      c25.5,0,47.3,9.5,65.4,28.6c18.1,19.1,27.1,42.1,27.1,69.1c0,12.6-1.9,24-5.7,34.3c16,7.6,28.8,19.3,38.3,35.1
      C507.2,231.5,512,249,512,268z M245.1,345.1l105.7-158.3c2.7-4.2,3.5-8.8,2.6-13.7c-1-4.9-3.5-8.8-7.7-11.4
      c-4.2-2.7-8.8-3.6-13.7-2.9c-5,0.8-9,3.2-12,7.4l-93.1,140L184,263.4c-3.8-3.8-8.2-5.6-13.1-5.4c-5,0.2-9.3,2-13.1,5.4
      c-3.4,3.4-5.1,7.7-5.1,12.9c0,5.1,1.7,9.4,5.1,12.9l58.9,58.9l2.9,2.3c3.4,2.3,6.9,3.4,10.3,3.4
      C236.6,353.7,241.7,350.9,245.1,345.1z"
      />
    </g>
  </svg>
);

const video: React.ReactNode = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42.6 23.5">
    <title>UGC_Video_Icon</title>
    <path d="M27.4,10.5l3.4-2V2.6A2.6,2.6,0,0,0,28.2,0H2.6A2.6,2.6,0,0,0,0,2.6V21a2.6,2.6,0,0,0,2.6,2.6H28.2A2.6,2.6,0,0,0,30.8,21V15.1l-3.4-2A1.4,1.4,0,0,1,27.4,10.5Z" />
    <path d="M40.3,4.4l-3,1.7L32.8,8.7l-3.1,1.8a1.4,1.4,0,0,0,0,2.6l3.1,1.8,4.5,2.6,3,1.7c1.3.8,2.3.2,2.3-1.3V5.7C42.6,4.2,41.6,3.7,40.3,4.4Z" />
  </svg>
);
const calender: React.ReactNode = (
  <svg height="512" viewBox="0 0 128 128" width="512" xmlns="http://www.w3.org/2000/svg">
    <g>
      <path
        d="m1.75 44.384h124.5a0 0 0 0 1 0 0v68.242a5 5 0 0 1 -5 5h-114.5a5 5 0 0 1 -5-5v-68.242a0 0 0 0 1 0 0z"
        fill="#e1e4fb"
      />
      <path
        d="m7.75 25.257h112.5a6 6 0 0 1 6 6v13.127a0 0 0 0 1 0 0h-124.5a0 0 0 0 1 0 0v-13.127a6 6 0 0 1 6-6z"
        fill="#4cb4e7"
      />
      <g fill="#4cb4e7">
        <path d="m19.125 10.374h4.034a4 4 0 0 1 4 4v10.883a0 0 0 0 1 0 0h-12.034a0 0 0 0 1 0 0v-10.883a4 4 0 0 1 4-4z" />
        <path d="m47.7 10.374h4.034a4 4 0 0 1 4 4v10.883a0 0 0 0 1 0 0h-12.034a0 0 0 0 1 0 0v-10.883a4 4 0 0 1 4-4z" />
        <path d="m76.269 10.374h4.031a4 4 0 0 1 4 4v10.883a0 0 0 0 1 0 0h-12.031a0 0 0 0 1 0 0v-10.883a4 4 0 0 1 4-4z" />
        <path d="m104.841 10.374h4.034a4 4 0 0 1 4 4v10.883a0 0 0 0 1 0 0h-12.034a0 0 0 0 1 0 0v-10.883a4 4 0 0 1 4-4z" />
      </g>
      <g fill="#c5c9f7">
        <rect height="11.928" rx="2" width="14.736" x="11.75" y="54.385" />
        <rect height="11.928" rx="2" width="14.736" x="41.671" y="54.385" />
        <rect height="11.928" rx="2" width="14.736" x="71.592" y="54.385" />
        <rect height="11.928" rx="2" width="14.736" x="101.514" y="54.385" />
        <rect height="11.928" rx="2" width="14.736" x="11.75" y="75.041" />
        <rect height="11.928" rx="2" width="14.736" x="41.671" y="75.041" />
        <rect height="11.928" rx="2" width="14.736" x="71.592" y="75.041" />
        <rect height="11.928" rx="2" width="14.736" x="101.514" y="75.041" />
        <rect height="11.928" rx="2" width="14.736" x="11.75" y="95.698" />
        <rect height="11.928" rx="2" width="14.736" x="41.671" y="95.698" />
      </g>
      <path
        d="m77.094 109.376a1.749 1.749 0 0 1 -1.371-.663l-5.5-6.94a1.749 1.749 0 1 1 2.741-2.173l4.1 5.177 7.3-9.544a1.75 1.75 0 0 1 2.781 2.125l-8.66 11.331a1.753 1.753 0 0 1 -1.375.687z"
        fill="#4cb4e7"
      />
      <path
        d="m107.016 109.376a1.751 1.751 0 0 1 -1.372-.663l-5.5-6.94a1.749 1.749 0 1 1 2.742-2.173l4.1 5.177 7.294-9.544a1.75 1.75 0 0 1 2.781 2.125l-8.659 11.331a1.753 1.753 0 0 1 -1.375.687z"
        fill="#4cb4e7"
      />
    </g>
  </svg>
);
const nextArrow: React.ReactNode = (
  <svg
    version="1.1"
    id="Layer_1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    x="0px"
    y="0px"
    viewBox="0 0 1280 1024"
    xmlSpace="preserve"
  >
    <g>
      <g>
        <path
          className="st0"
          d="M317.8,964.1c14.3-14.3,28.7-28.7,43-43c34.3-34.3,68.6-68.6,102.9-102.9c41.5-41.5,82.9-82.9,124.4-124.4
			c35.8-35.8,71.7-71.7,107.5-107.5c17.4-17.4,35.1-34.6,52.3-52.3c0.2-0.2,0.5-0.5,0.7-0.7c11.5-11.5,11.5-30.9,0-42.4
			c-14.3-14.3-28.7-28.7-43-43C671.3,413.6,637,379.3,602.7,345c-41.5-41.5-82.9-82.9-124.4-124.4c-35.8-35.8-71.7-71.7-107.5-107.5
			c-17.4-17.4-34.6-35.1-52.3-52.3c-0.2-0.2-0.5-0.5-0.7-0.7c-11.1-11.1-31.5-11.9-42.4,0c-11,12-11.9,30.6,0,42.4
			c14.3,14.3,28.7,28.7,43,43c34.3,34.3,68.6,68.6,102.9,102.9c41.5,41.5,82.9,82.9,124.4,124.4c35.8,35.8,71.7,71.7,107.5,107.5
			c17.4,17.4,34.5,35.3,52.3,52.3c0.3,0.2,0.5,0.5,0.7,0.7c0-14.1,0-28.3,0-42.4c-14.3,14.3-28.7,28.7-43,43
			c-34.3,34.3-68.6,68.6-102.9,102.9c-41.5,41.5-82.9,82.9-124.4,124.4C400,797,364.2,832.8,328.3,868.7
			c-17.4,17.4-35.1,34.6-52.3,52.3c-0.2,0.2-0.5,0.5-0.7,0.7c-11.1,11.1-11.9,31.5,0,42.4C287.3,975.1,305.9,976,317.8,964.1
			L317.8,964.1z"
        />
      </g>
    </g>
  </svg>
);
const messageEnvelope: React.ReactNode = (
  <svg
    version="1.1"
    id="Capa_1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    x="0px"
    y="0px"
    viewBox="0 0 477.867 477.867"
    xmlSpace="preserve"
  >
    <g>
      <g>
        <path d="M460.8,68.267H17.067l221.867,182.75L463.309,68.779C462.488,68.539,461.649,68.368,460.8,68.267z" />
      </g>
    </g>
    <g>
      <g>
        <path
          d="M249.702,286.31c-6.288,5.149-15.335,5.149-21.623,0L0,98.406v294.127c0,9.426,7.641,17.067,17.067,17.067H460.8
			c9.426,0,17.067-7.641,17.067-17.067V100.932L249.702,286.31z"
        />
      </g>
    </g>
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
  </svg>
);
const goToSite: React.ReactNode = (
  <svg
    version="1.1"
    id="Layer_1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    x="0px"
    y="0px"
    viewBox="0 0 32 40"
    xmlSpace="preserve"
    className="goToHome"
  >
    <path
      className="st0"
      d="M24,6H12c-1.1,0-2,0.9-2,2c0,1.1,0.9,2,2,2h7.2L6.6,22.6c-0.8,0.8-0.8,2,0,2.8c0.8,0.8,2,0.8,2.8,0L22,12.8V20
	c0,1.1,0.9,2,2,2s2-0.9,2-2V8C26,6.9,25.1,6,24,6z"
    />
  </svg>
);

const iconPlus: React.ReactNode = (
  <svg
    version="1.1"
    id="Capa_1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    x="0px"
    y="0px"
    viewBox="0 0 512 512"
    xmlSpace="preserve"
  >
    <g>
      <g>
        <path
          d="M256,0C114.84,0,0,114.84,0,256s114.84,256,256,256s256-114.84,256-256S397.16,0,256,0z M256,475.429
			c-120.997,0-219.429-98.432-219.429-219.429S135.003,36.571,256,36.571S475.429,135.003,475.429,256S376.997,475.429,256,475.429z
			"
        />
      </g>
    </g>
    <g>
      <g>
        <path
          d="M256,134.095c-10.1,0-18.286,8.186-18.286,18.286v207.238c0,10.1,8.186,18.286,18.286,18.286
			c10.1,0,18.286-8.186,18.286-18.286V152.381C274.286,142.281,266.1,134.095,256,134.095z"
        />
      </g>
    </g>
    <g>
      <g>
        <path
          d="M359.619,237.714H152.381c-10.1,0-18.286,8.186-18.286,18.286c0,10.1,8.186,18.286,18.286,18.286h207.238
			c10.1,0,18.286-8.186,18.286-18.286C377.905,245.9,369.719,237.714,359.619,237.714z"
        />
      </g>
    </g>
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
    <g />
  </svg>
);
const searchDrop: React.ReactNode = (
  <svg
    version="1.1"
    id="Layer_1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    x="0px"
    y="0px"
    viewBox="0 0 1024 1280"
    xmlSpace="preserve"
  >
    <g>
      <g>
        <path
          className="st0"
          d="M317.8,964.1c14.3-14.3,28.7-28.7,43-43c34.3-34.3,68.6-68.6,102.9-102.9c41.5-41.5,82.9-82.9,124.4-124.4
			c35.8-35.8,71.7-71.7,107.5-107.5c17.4-17.4,35.1-34.6,52.3-52.3c0.2-0.2,0.5-0.5,0.7-0.7c11.5-11.5,11.5-30.9,0-42.4
			c-14.3-14.3-28.7-28.7-43-43C671.3,413.6,637,379.3,602.7,345c-41.5-41.5-82.9-82.9-124.4-124.4c-35.8-35.8-71.7-71.7-107.5-107.5
			c-17.4-17.4-34.6-35.1-52.3-52.3c-0.2-0.2-0.5-0.5-0.7-0.7c-11.1-11.1-31.5-11.9-42.4,0c-11,12-11.9,30.6,0,42.4
			c14.3,14.3,28.7,28.7,43,43c34.3,34.3,68.6,68.6,102.9,102.9c41.5,41.5,82.9,82.9,124.4,124.4c35.8,35.8,71.7,71.7,107.5,107.5
			c17.4,17.4,34.5,35.3,52.3,52.3c0.3,0.2,0.5,0.5,0.7,0.7c0-14.1,0-28.3,0-42.4c-14.3,14.3-28.7,28.7-43,43
			c-34.3,34.3-68.6,68.6-102.9,102.9c-41.5,41.5-82.9,82.9-124.4,124.4C400,797,364.2,832.8,328.3,868.7
			c-17.4,17.4-35.1,34.6-52.3,52.3c-0.2,0.2-0.5,0.5-0.7,0.7c-11.1,11.1-11.9,31.5,0,42.4C287.3,975.1,305.9,976,317.8,964.1
			L317.8,964.1z"
        />
      </g>
    </g>
  </svg>
);

export let SVGS: {
  [index: string]: any;
} = {};

SVGS[`${GRAPHQL_TYPE_RADIVISION_POST}`] = articleIcon; //radivisionPostIcon;
SVGS[`${GRAPHQL_TYPE_ARTICLE_STORY}`] = externalLinkAlt; //articleIcon;
SVGS[`${GRAPHQL_TYPE_BOOK_STORY}`] = bookIcon;
SVGS[`${GRAPHQL_TYPE_NEWSLETTER_STORY}`] = newsletterIcone;
SVGS[`${GRAPHQL_TYPE_ONLINE_COURSE_STORY}`] = onlineCourseIcon;
SVGS[`${GRAPHQL_TYPE_PODCAST_STORY}`] = micIcon;
SVGS[`${GRAPHQL_TYPE_PERSON}`] = personIcon;
SVGS[`${GRAPHQL_TYPE_ORIGINAL_CONTENT_STORY}`] = originalContentIcon;
SVGS[`${GRAPHQL_TYPE_ENTITY}`] = entityIcon;

/**
 * the Svg Elements interface
 *
 * @interface
 */
export interface SvgElements {
  arrowsAllDirections?: React.ReactNode;
  attention?: React.ReactNode;
  audio?: React.ReactNode;
  audioChecked?: React.ReactNode;
  book?: React.ReactNode;
  bRollVideo?: React.ReactNode;
  bRollVideoChecked?: React.ReactNode;
  caretLeft?: React.ReactNode;
  caretRight?: React.ReactNode;
  chat?: React.ReactNode;
  checkmark?: React.ReactNode;
  copy?: React.ReactNode;
  market?: React.ReactNode;
  stop?: React.ReactNode;
  chevronDown?: React.ReactNode;
  chevronLeft?: React.ReactNode;
  chevronRight?: React.ReactNode;
  chevronLeftThin?: React.ReactNode;
  chevronRightThin?: React.ReactNode;
  bulb?: React.ReactNode;
  cogs?: React.ReactNode;
  portfolio?: React.ReactNode;
  portfolioPeople?: React.ReactNode;
  crosshairs?: React.ReactNode;
  miniPlayer?: React.ReactNode;
  crosshairsCircle?: React.ReactNode;
  crosshairsThin?: React.ReactNode;
  crosshairsThinAlt?: React.ReactNode;
  currency?: React.ReactNode;
  dash?: React.ReactNode;
  dna?: React.ReactNode;
  dots?: React.ReactNode;
  dotsThin?: React.ReactNode;
  editSquare?: React.ReactNode;
  emptyInbox?: React.ReactNode;
  eyeClosed?: React.ReactNode;
  eyeOpened?: React.ReactNode;
  externalLink?: React.ReactNode;
  externalLinkAlt?: React.ReactNode;
  facebook?: React.ReactNode;
  facebookCircle?: React.ReactNode;
  flag?: React.ReactNode;
  hamburger?: React.ReactNode;
  home?: React.ReactNode;
  image?: React.ReactNode;
  images?: React.ReactNode;
  info?: React.ReactNode;
  instagram?: React.ReactNode;
  library?: React.ReactNode;
  link?: React.ReactNode;
  linkedin?: React.ReactNode;
  linkedinSquare?: React.ReactNode;
  linkedinModal?: React.ReactNode;
  location?: React.ReactNode;
  maximize?: React.ReactNode;
  media?: React.ReactNode;
  medium?: React.ReactNode;
  pinterest?: React.ReactNode;
  minimize?: React.ReactNode;
  newsletter?: React.ReactNode;
  nominateStars?: React.ReactNode;
  onlineCourse?: React.ReactNode;
  overview?: React.ReactNode;
  pause?: React.ReactNode;
  pauseCircle?: React.ReactNode;
  pencil?: React.ReactNode;
  people?: React.ReactNode;
  peopleAlt?: React.ReactNode;
  peopleThin?: React.ReactNode;
  personAlt?: React.ReactNode;
  play?: React.ReactNode;
  muted?: React.ReactNode;
  unmuted?: React.ReactNode;
  playCircle?: React.ReactNode;
  playCircleSolid?: React.ReactNode;
  playSquare?: React.ReactNode;
  playWithSpaces?: React.ReactNode;
  plus?: React.ReactNode;
  plusAlt?: React.ReactNode;
  profilePreview?: React.ReactNode;
  radivisionPlay?: React.ReactNode;
  radivisionStar?: React.ReactNode;
  reddit?: React.ReactNode;
  angellist?: React.ReactNode;
  other?: React.ReactNode;
  globe?: React.ReactNode;
  weibo?: React.ReactNode;
  whatsapp?: React.ReactNode;
  snapshat?: React.ReactNode;
  redHeart?: React.ReactNode;
  reply?: React.ReactNode;
  rocket?: React.ReactNode;
  search?: React.ReactNode;
  searchAlt?: React.ReactNode;
  send?: React.ReactNode;
  socialPost?: React.ReactNode;
  spinner?: React.ReactNode;
  star?: React.ReactNode;
  swap?: React.ReactNode;
  trash?: React.ReactNode;
  tumblr?: React.ReactNode;
  twitter?: React.ReactNode;
  verified?: React.ReactNode;
  lock?: React.ReactNode;
  video?: React.ReactNode;
  calender?: React.ReactNode;
  nextArrow?: React.ReactNode;
  messageEnvelope?: React.ReactNode;
  google?: React.ReactNode;
  goToSite?: React.ReactNode;
  iconPlus?: React.ReactNode;
  searchDrop?: React.ReactNode;
  youtube?: React.ReactNode;
}
export const SVG: SvgElements = {
  angellist,
  attention,
  arrowsAllDirections,
  audio,
  audioChecked,
  book,
  bulb,
  calender,
  caretLeft,
  caretRight,
  chat,
  checkmark,
  copy,
  market,
  chevronDown,
  chevronLeft,
  chevronLeftThin,
  chevronRight,
  chevronRightThin,
  cogs,
  crosshairs,
  crosshairsCircle,
  crosshairsThin,
  crosshairsThinAlt,
  currency,
  dash,
  dna,
  dots,
  dotsThin,
  editSquare,
  emptyInbox,
  externalLink,
  externalLinkAlt,
  eyeClosed,
  eyeOpened,
  facebook,
  facebookCircle,
  flag,
  goToSite,
  google,
  hamburger,
  home,
  iconPlus,
  image,
  images,
  info,
  instagram,
  library,
  link,
  linkedin,
  linkedinModal,
  linkedinSquare,
  lock,
  maximize,
  media,
  medium,
  messageEnvelope,
  miniPlayer,
  minimize,
  muted,
  newsletter,
  nextArrow,
  nominateStars,
  onlineCourse,
  overview,
  pause,
  pauseCircle,
  pencil,
  people,
  peopleAlt,
  peopleThin,
  personAlt,
  pinterest,
  play,
  playCircle,
  playCircleSolid,
  playSquare,
  playWithSpaces,
  plus,
  plusAlt,
  portfolio,
  portfolioPeople,
  profilePreview,
  radivisionPlay,
  radivisionStar,
  redHeart,
  reddit,
  reply,
  search,
  searchAlt,
  searchDrop,
  send,
  snapchat,
  socialPost,
  spinner,
  star,
  stop,
  swap,
  trash,
  tumblr,
  twitter,
  unmuted,
  verified,
  video,
  weibo,
  other,
  globe,
  whatsapp,
  youtube,
};
